import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import { useSnackbar } from 'notistack';
import styled from "@emotion/styled";
import CloseIcon from '@mui/icons-material/Close';
import StraightenIcon from '@mui/icons-material/Straighten';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Portal } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';

// Redux actions
import {
  addToWishlist,
  fetchWishlist,
  fetchCart,
  performAdvanceFilter,
  fetchCarsByModelName,
} from "./store";

// MUI components
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Paper,
  Button,
  IconButton,
  Chip,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Rating,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Pagination,
  Select,
  MenuItem,
  Tooltip,
  Breadcrumbs,
  Link,
  TextField,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Modal,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Radio,
  FormControl,
  InputLabel,
  Fade,
  TableHead,
  Button as MuiButton,
  InputAdornment,
} from "@mui/material";

// MUI icons
import {
  Favorite,
  FavoriteBorder,
  DirectionsCar,
  GridView,
  List as ListIcon,
  Share,
  Search as SearchIcon,
} from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FilterListIcon from "@mui/icons-material/FilterList";

// Local components (Assuming these exist in your project structure)
import Notes from "./header";
import BasicSelectFilter from "./filter";
import FooterManual from "./footerManual";
import InquiryModal from "./inquiryModal";


// Styled Components from ExploreByGlobalSearch
const StyledTableContainer = styled(TableContainer)`
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow-x: auto;
`;

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const StyledTableHeaderCell = styled(TableCell)`
  background-color: #f5f5f5;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const StyledCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const StickyFilterContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: white;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;
  transform: ${(props) =>
    props.visible ? "translateY(0)" : "translateY(-100%)"};
`;

const SpinningLogo = styled("img")(({ theme }) => ({
  width: "150px",
  height: "150px",
}));

// Utility function to remove duplicate brand name from model name
const cleanModelName = (brandName, modelName) => {
  if (!brandName || !modelName) return modelName || '';

  // Convert both to lowercase for case-insensitive comparison
  const brandLower = brandName.toLowerCase().trim();
  const modelLower = modelName.toLowerCase().trim();

  // Check if model name starts with brand name
  if (modelLower.startsWith(brandLower)) {
    // Remove the brand name from the beginning
    return modelName.substring(brandName.length).trim();
  }

  // Return original model name if no duplicate found
  return modelName;
};

// Car Card for List View (Grid)
const CarListingCardGrid = ({
  car,
  onCarClick,
  onAddToWishlist,
  onAddToCart,
  imageUrl,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { modelId } = useParams();
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const isFavorite = wishlistItems.some((item) => item.modelId === car.carModelId);
  const [selectedCarModelId, setSelectedCarModelId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Dialog and Modal Handling
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [open, setOpen] = useState(false);

  // Product and Loading
  const [product, setProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(true);

  // Inquiry and Form
  const [inquiryType, setInquiryType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isPortSelected, setIsPortSelected] = useState(false);
  const [openSpecifications, setOpenSpecifications] = useState(false);
  const [shippingCostData, setShippingCostData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phoneNumber: "",
    description: "",
    country: null,
    port: null,
    userMessage: '',
    shippingOption: '',
    shippingType: null,
    containerType: null,
    containerShippingOption: null
  });

  const handleShippingTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      shippingType: type,
      shippingOption: null,
      containerType: null,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerSelection = (containerType) => {
    setFormData(prev => ({
      ...prev,
      containerType,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerShippingOptionSelection = async (option) => {
    setFormData(prev => ({ ...prev, containerShippingOption: option }));

    if (formData.port && formData.containerType && option) {
      try {
        const shippingData = await calculateShippingPrice({
          carModelId: product?.carModelId || '',
          portId: formData.port?.portId || '',
          shippingOptionType: option,
          isInquiry: false,
          isContainerShipping: true,
          is20ft: formData.containerType === '20ft',
          is40ft: formData.containerType === '40ft',
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          mobileNo: formData.phoneNumber || '',
          emailId: formData.emailId || '',
          userMessage: formData.userMessage || ''
        });

        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: shippingData.volumeCubicMeters,
            totalCost: shippingData.totalCost,
            totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
            containerRate: shippingData.containerRate,
            containerType: shippingData.containerType,
            isContainerShipping: shippingData.isContainerShipping,
            error: null
          }
        }));
      } catch (error) {
        console.error('Error calculating container shipping:', error);
        if (error.response?.status === 401) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'UNAUTHORIZED'
            }
          }));
        } else if (error.response?.status === 404 || error.message?.includes('Container rate not found')) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'CONTAINER_RATE_NOT_FOUND'
            }
          }));
        }
      }
    }
  };

  // Add helper function to get selected option (around line 500)
  const getSelectedOption = () => {
    return formData.shippingType === 'container' ? formData.containerShippingOption : formData.shippingOption;
  };

  // Add helper function to check if shipping cost should show "Ask" (around line 505)
  const shouldShowAsk = () => {
    if (formData.shippingType === 'container') {
      return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
        formData.shippingCalculation?.error === 'UNAUTHORIZED' ||
        !formData.shippingCalculation?.totalCost;
    } else {
      const hasDimensionIssue = (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
        (!product?.carLength || !product?.carWidth || !product?.carHeight);
      const hasUnauthorizedError = formData.shippingCalculation?.error === 'UNAUTHORIZED';
      return hasDimensionIssue || hasUnauthorizedError;
    }
  };

  // Helper function to check if shipping cost should show "Ask" specifically
  const shouldShowShippingCostAsk = () => {
    if (formData.shippingType === 'container') {
      return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
        formData.shippingCalculation?.error === 'UNAUTHORIZED';
    } else {
      const hasDimensionIssue = (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
        (!product?.carLength || !product?.carWidth || !product?.carHeight);
      const hasUnauthorizedError = formData.shippingCalculation?.error === 'UNAUTHORIZED';
      return hasDimensionIssue || hasUnauthorizedError;
    }
  };

  // Update the handleSubmitShippingInquiry function (around line 430)
  const handleSubmitShippingInquiry = async () => {
    if (!formData.port || (!formData.shippingOption && !formData.containerShippingOption)) {
      setShowErrorPopup(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const isContainer = formData.shippingType === 'container';

      const shippingData = {
        carModelId: product?.carModelId,
        portId: formData.port.portId,
        shippingOptionType: isContainer ? formData.containerShippingOption : formData.shippingOption,
        isInquiry: true,
        isContainerShipping: isContainer,
        is20ft: isContainer && formData.containerType === '20ft',
        is40ft: isContainer && formData.containerType === '40ft',
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNo: formData.phoneNumber,
        emailId: formData.emailId,
        userMessage: formData.userMessage
      };

      const response = await calculateShippingPrice(shippingData);
      setShowSuccessPopup(true);

      setFormData(prev => ({
        ...prev,
        shippingCalculation: {
          ...prev.shippingCalculation,
          ...response
        }
      }));

    } catch (error) {
      console.error('Error submitting shipping inquiry:', error);
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);

  // Helper Functions
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateValidImageUrls = async (type, modelId, maxCount = 15) => {
    const baseUrl = `https://car.amar.co.jp/api/image/car-${type}-images-`;

    const urls = await Promise.allSettled(
      Array.from({ length: maxCount }, (_, i) =>
        checkImageExists(`${baseUrl}${i + 1}/get-image/${modelId}`)
      )
    );

    return urls
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);
  };

  const calculateShippingPrice = async (data) => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate-price', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to calculate shipping price');
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating shipping price:', error);
      throw error;
    }
  };

  const fetchUserDetailsGlobal = async () => {
    try {
      const token = localStorage.getItem('authToken') || 'eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJoYXJkaWtAZ3JldHRvbS5jb20iLCJleHAiOjE3NTQ4NzY2NjEsImlhdCI6MTc1MTI3NjY2MSwiYXV0aG9yaXRpZXMiOiIifQ.caR99Ex3qtHfp9tLCMbsDxf9uxt_KhK9IlDbrB9-rek498PHySU1-FQhmBmTtK947DnUBXKS6CwNy6PccCH-4A';
      const response = await fetch('https://car.amar.co.jp/api/authorization/user/get/user/details', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const userData = await response.json();
      return userData;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };

  // Handle state updates
  const handlePortChange = (event, newValue) => {
    handleChange({ target: { name: 'port', value: newValue } });
    setIsPortSelected(!!newValue);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && { port: null })
    }));
  };

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  // Inquiry/Submit Handlers
  const sendGeneralInquiry = async (token, currentModelId) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailId: formData.emailId,
      phoneNumber: formData.phoneNumber,
      description: formData.userMessage,
      modelId: parseInt(currentModelId, 10)
    };

    const response = await fetch('https://car.amar.co.jp/api/dashboard/inquiry/send-inquiry', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Inquiry failed');
  };

  const handleSubmitInquiry = async () => {
    const token = localStorage.getItem("authToken");
    const currentModelId = selectedCarModelId || car?.carModelId;

    if (!token) {
      navigate("/login");
      return;
    }

    if (!currentModelId) {
      enqueueSnackbar('Car model information is missing. Please try again.', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);

    try {
      if (inquiryType === 'general') {
        await sendGeneralInquiry(token, currentModelId);
        setShowSuccessPopup(true);
      } else {
        await handleSubmitShippingInquiry();
      }
    } catch (error) {
      console.error('Inquiry submission error:', error);
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalOpen = () => {
    setSelectedCarModelId(car.carModelId);
    setOpen(true);
  };

  const handleModalClose = () => setOpen(false);

  const handleConfirmMove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) { navigate("/login"); return; }
      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${car.carModelId}&SelectType=MOVE_TO_CART`,
        { method: "POST", headers: { accept: "application/json", "Accept-Language": "en", Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        enqueueSnackbar("Item moved to cart successfully", { variant: "success" });
        dispatch(fetchWishlist());
        dispatch(fetchCart());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to move item to cart", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error moving item to cart", { variant: "error" });
    } finally { setOpenDialog(false); }
  };

  const handleConfirmRemove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) { navigate("/login"); return; }
      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${car.carModelId}`,
        { method: "DELETE", headers: { accept: "application/json", "Accept-Language": "en", Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        enqueueSnackbar("Item removed from wishlist successfully", { variant: "success" });
        dispatch(fetchWishlist());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to remove item from wishlist", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error removing item from wishlist", { variant: "error" });
    } finally { setOpenDialog(false); }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("authToken");
      if (!token) { navigate("/login"); return; }
      if (isFavorite) {
        setDialogType("remove");
        setOpenDialog(true);
        return;
      }
      const result = await dispatch(addToWishlist(car.carModelId));
      if (result.success) {
        enqueueSnackbar("Added to wishlist successfully", { variant: "success" });
      } else {
        enqueueSnackbar(result.message || "Failed to add to wishlist", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to wishlist", { variant: "error" });
    }
  };

  // Effects
  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        const response = await fetchUserDetailsGlobal();
        const userDetails = response.userDetails;
        setFormData(prevData => ({
          ...prevData,
          firstName: userDetails.firstName || '', lastName: userDetails.lastName || '',
          phoneNumber: userDetails.userMobileNumber || '', emailId: userDetails.email || ''
        }));
      } catch (error) { /* handle error silently */ }
    };

    if (inquiryType === 'general' && !formData.firstName) {
      loadUserDetails();
    }
  }, [inquiryType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchProductData = async () => {
      const currentModelId = selectedCarModelId || car?.carModelId;
      if (!currentModelId) return;
      try {
        setProductLoading(true);
        const response = await axios.get(
          `https://car.amar.co.jp/api/dashboard/product/get/Model/details/${currentModelId}`
        );
        if (response.data && response.data.data) { setProduct(response.data.data); } else { setProduct(null); }
        setProductLoading(false);
      } catch (err) { setProductLoading(false); setProduct(null); }
    };
    if (open && selectedCarModelId) { fetchProductData(); }
  }, [open, selectedCarModelId, car?.carModelId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentId = selectedCarModelId || car?.carModelId;
    if (!currentId) return;
    const loadExteriorImages = async () => {
      const exteriors = await generateValidImageUrls("exterior", currentId);
      setExteriorImages(exteriors);
    };
    loadExteriorImages();
  }, [selectedCarModelId, car?.carModelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Constants
  const [exteriorImages, setExteriorImages] = useState([]);
  const imageUrl1 = exteriorImages[0] || imageUrl || "carousel/carslider.png";
  const shippingOptions = [
    { value: "CIF", label: "CIF (Cost, Insurance and Freight)" },
    { value: "CNF", label: "C&F (Cost and Freight)" },
    { value: "FOB", label: "FOB (Free On Board)" }
  ];
  const labelMap = {
    firstName: "First Name *", lastName: "Last Name *",
    phoneNumber: "Phone Number *", emailId: "Email Address *"
  };

  return (
    <StyledCard
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.15)", transform: "translateY(-4px)" }, transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)" }}
    >
      {/* Image Section with Hover Effect */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
        onClick={() => onCarClick(car.carModelId)}
      >
        <CardMedia
          component="img"
          // height="100%"
          image={imageUrl}
          alt={`${car.carBrandName} ${car.carModelName}`}
          sx={{
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.05)" : "scale(1)",
            // objectFit: "cover",
            objectFit: isMobile ? "contain" : "cover",
            width: "100%",
            height: "100%",
          }}
          onClick={() => onCarClick(car.carModelId)}
        />

        {/* Favorite Button */}
        <IconButton
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: isMobile ? "4px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
          onClick={handleFavoriteClick}
        >
          {isFavorite ? (
            <Favorite
              sx={{
                color: "red",
                fontSize: isMobile ? "1rem" : "1.25rem",
                animation: isFavorite ? "pulse 0.5s ease" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
          ) : (
            <FavoriteBorder
              sx={{
                fontSize: isMobile ? "1rem" : "1.25rem",
                color: theme.palette.grey[600],
              }}
            />
          )}
        </IconButton>
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          flexGrow: 1,
          p: isMobile ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => onCarClick(car.carModelId)}
      >
        {/* Title and Basic Info */}
        <Box >
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              fontWeight: 700,
              // lineHeight: 1.2,
              mb: 0.5,
              color: theme.palette.text.primary,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // minHeight: isMobile ? '2.4rem' : '2.7rem', // Ensure consistent height
            }}
          >
            {car.carBrandName} {cleanModelName(car.carBrandName, car.carModelName)} {car.year}
          </Typography>
        </Box>

        {/* Specifications Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,

            "& > div": {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            },
          }}
        >
          {/* Ref # */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Ref #:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carRefNumber || "N/A"}
            </Typography>
          </Box>

          {/* Year */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Year:
            </Typography>
            {car.year && <span>{car.year}</span>}
          </Box>

          {/* Engine */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Engine:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carEngineValue || "N/A"}
            </Typography>
          </Box>

          {/* Fuel and Transmission Chips */}
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Fuel:
            </Typography>

            {car.carFuel && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                  color: theme.palette.text.primary,
                }}
              >
                {car.carFuel}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Price Section */}
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
              fontSize: isMobile ? "0.65rem" : "20px",
            }}
          >
            Price:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "20px",
              fontWeight: 700,
              color: theme.palette.error.main,
            }}
          >
            {car.carPrice
              ? `$${car.carPrice.toLocaleString()}`
              : "Price on request"}
          </Typography>
        </Box>
      </CardContent>

      {/* Action Buttons */}
      <Box
        sx={{
          p: isMobile ? 1 : 1.5,
          pt: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          size="medium" // Already using small size
          sx={{
            bgcolor: "red",
            color: "white",
            border: "1px solid red",
            borderRadius: "50px", // Smaller border radius
            fontSize: "0.75rem", // Smaller font size
            fontWeight: "600",
            padding: "18px 18px", // Reduced padding
            minWidth: "150px", // Reduced minimum width
            height: "38px", // Explicitly set smaller height
            width: "100%", // Full width
            transition: "all 0.3s ease",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "red",
              transform: "translateX(-100%)",
              transition: "transform 0.3s ease",
              zIndex: -1,
            },
            "&:hover": {
              color: "white",
              boxShadow: "0 3px 6px rgba(255,0,0,0.3)", // Smaller shadow
              "&::before": {
                transform: "translateX(0)",
              },
            },
          }}
          onClick={(e) => { e.stopPropagation(); setSelectedCarModelId(car.carModelId); handleModalOpen(); }}
        >
          INQUIRY
        </Button>
      </Box>

      {/* Inquiry Modal */}
      <Modal open={open} onClose={handleModalClose} aria-labelledby="inquiry-modal-title" aria-describedby="inquiry-modal-description">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
          <Container
            maxWidth="md"
            sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "white", borderRadius: "8px", boxShadow: 24, width: "90%", maxHeight: "90vh", overflow: "auto", p: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ maxHeight: "80vh", overflowY: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", position: "sticky", borderBottom: "1px solid #cccccc", top: 0, backgroundColor: "#fff", zIndex: 10, padding: "10px 20px" }}>
                <Typography variant="h4" fontWeight="bold" sx={{ margin: 0, fontSize: "24px" }}>Get a Free Quotation</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Box sx={{ textAlign: "right", display: "flex", alignItems: "center", gap: "10px" }}>
                    <Typography variant="body2" color="#666" fontWeight="500" sx={{ fontSize: "20px" }}>
                      {formData.shippingOption ? `Total ${formData.shippingOption} Cost` : "Total Cost"}
                    </Typography>
                    <Typography variant="h6" color="#ff3b30" fontWeight="700" sx={{ fontSize: "22px" }}>
                      {formData.shippingCalculation?.totalCostIncludingBasePrice ? `$${formData.shippingCalculation.totalCostIncludingBasePrice.toFixed(2)}` : `$${product?.carPrice.toFixed(2) || "0.00"}`}
                    </Typography>
                  </Box>
                  <IconButton onClick={handleModalClose} sx={{ left: "30px" }}><CloseIcon /></IconButton>
                </Box>
              </Box>
              {productLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}><CircularProgress /></Box>
              ) : product ? (
                <>
                  <Grid container spacing={4} sx={{ marginTop: "5px", padding: '0 20px' }}>
                    <Grid item xs={12} md={6}>
                      <img src={imageUrl1} alt={`${product.brandName || ''} ${product.modelName || ''}`} style={{ width: "100%", borderRadius: '8px', objectFit: 'cover' }} onError={(e) => { e.target.src = "carousel/carslider.png"; }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h4" fontWeight="bold" mt={2}>{`${product.brandName} ${product.modelName} ${product.categoriesName || ''}`}</Typography>
                      <Box sx={{ marginTop: "15px", display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {car.ourPick && (<Chip label="Our Pick" size="small" sx={{ fontSize: "0.65rem", height: "26px", color: "#dd006e", backgroundColor: "#ffbddf", fontWeight: "bold", border: "1px solid #FF69B4" }} />)}
                        {car.carGrade && (<Chip label={`Grade ${car.carGrade || "A"}`} size="small" sx={{ fontSize: "0.65rem", height: "26px", color: "#009966", backgroundColor: "#9fffe7", fontWeight: "bold", border: "1px solid #00FFAA" }} />)}
                        {car.specialDeal && (<Chip label="Special Offer" size="small" sx={{ fontSize: "0.65rem", height: "26px", backgroundColor: "#d09fff", color: "#6c2aab", fontWeight: "bold", border: "1px solid #6c2aab" }} />)}
                      </Box>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ marginTop: '12px' }}>{product ? `${product.carMileage} | ${product.carTransmission} | ${product.carDrive} | ${product.carEngineValue}` : 'Loading...'}</Typography>
                      <Typography variant="body1" sx={{ marginTop: '24px' }}>{product ? `${product.categoriesName} with ${product.carColor} exterior` : 'Loading...'}</Typography>
                      <Box sx={{ mt: 3 }}><Typography variant="h4" color="#ff3b30" fontWeight="bold" sx={{ marginTop: '8px' }}>{product ? `$${product.carPrice.toFixed(2)}` : '$0.00'}</Typography></Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: "flex", justifyContent: "center", mb: 2, marginTop: "30px", padding: '0 20px' }}>
                    <Button variant="contained" onClick={() => setOpenSpecifications((prevState) => !prevState)} sx={{ bgcolor: "#ff3b30", "&:hover": { bgcolor: "#e03228" }, display: "flex", alignItems: "center", gap: 1 }}>
                      {openSpecifications ? (<>View Less Specifications <ExpandLessIcon /></>) : (<>View More Specifications <ExpandMoreIcon /></>)}
                    </Button>
                  </Box>

                  {openSpecifications && (
                    <TableContainer component={Paper} sx={{ padding: '0 20px' }}>
                      <Table><TableBody>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Reference No.</TableCell><TableCell>{product.carRefNumber || '-'}</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Drive Type</TableCell><TableCell>{product.carDrive || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Registration Year</TableCell><TableCell>{product.year || '-'}</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Model Code</TableCell><TableCell>{product.carModelCode || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Manufacture Date</TableCell><TableCell>{product.carManufactureDate || '-'}</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Model Grade</TableCell><TableCell>{product.carGrade || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Mileage</TableCell><TableCell>{product.carMileage || '-'}<br />(Approx. {Math.round((product.carMileage || 0) * 0.621371)} miles)</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Transmission</TableCell><TableCell>{product.carTransmission || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Fuel Type</TableCell><TableCell>{product.carFuel || '-'}</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Engine Capacity</TableCell><TableCell>{product.carEngineValue || '-'}cc<br />({((product.carEngineValue || 0) / 1000).toFixed(2)} liters)</TableCell></TableRow>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>No. of Doors</TableCell><TableCell>{product.carDoor || '-'}</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>No. of Seats</TableCell><TableCell>{product.carSeat || '-'}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Steering</TableCell><TableCell>{product.carSteering || '-'}</TableCell><TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>Color</TableCell><TableCell>{product.carColor || '-'}</TableCell></TableRow>
                      </TableBody></Table>
                    </TableContainer>
                  )}
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, display: "flex", justifyContent: "center", padding: '0 20px' }}>* Required fields</Typography>

                  <Card sx={{ background: 'white', border: '2px solid #ff3b30', borderRadius: '16px', padding: { xs: '16px', md: '24px' }, maxWidth: '800px', margin: 'auto', position: 'relative', overflow: 'visible', boxShadow: '0 8px 16px rgba(255, 59, 48, 0.2)', mb: 4, '&:before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '16px', border: '1px solid rgba(255, 59, 48, 0.2)', zIndex: -1 } }}>
                    <CardContent>
                      <Typography variant="h5" sx={{ color: '#ff3b30', mb: 3, textAlign: 'center', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '1px', position: 'relative', '&:after': { content: '""', position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: '60px', height: '2px', backgroundColor: '#ff3b30' } }}>Vehicle Inquiry Form</Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
                        <Button variant={inquiryType === 'general' ? 'contained' : 'outlined'} onClick={() => setInquiryType('general')} sx={{ backgroundColor: inquiryType === 'general' ? '#ff3b30' : 'transparent', color: inquiryType === 'general' ? 'white' : '#ff3b30', border: '1.5px solid #ff3b30', borderRadius: '6px', padding: '8px 16px', fontWeight: 'bold', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px', transition: 'all 0.2s', minWidth: '120px', '&:hover': { backgroundColor: inquiryType === 'general' ? '#ff5047' : 'rgba(255, 59, 48, 0.1)', borderColor: '#ff3b30' } }}>General Inquiry</Button>
                        <Button variant={inquiryType === 'shipping' ? 'contained' : 'outlined'} onClick={() => setInquiryType('shipping')} sx={{ backgroundColor: inquiryType === 'shipping' ? '#ff3b30' : 'transparent', color: inquiryType === 'shipping' ? 'white' : '#ff3b30', border: '1.5px solid #ff3b30', borderRadius: '6px', padding: '8px 16px', fontWeight: 'bold', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px', transition: 'all 0.2s', minWidth: '160px', '&:hover': { backgroundColor: inquiryType === 'shipping' ? '#ff5047' : 'rgba(255, 59, 48, 0.1)', borderColor: '#ff3b30' } }}>Shipping Quote</Button>
                      </Box>

                      {/* General Inquiry Form */}
                      {inquiryType === 'general' && (
                        <Box sx={{ backgroundColor: 'rgba(255, 59, 48, 0.05)', borderRadius: '10px', padding: '16px', border: '1px dashed rgba(255, 59, 48, 0.2)' }}>
                          <Typography variant="h6" sx={{ color: '#ff3b30', mb: 2, fontWeight: '600', display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}><MailOutlineIcon fontSize="small" /> General Inquiry Form</Typography>
                          <Grid container spacing={2}>
                            {['firstName', 'lastName', 'phoneNumber', 'emailId'].map((field) => (
                              <Grid item xs={12} md={6} key={field}>
                                <TextField fullWidth size="small" id={field} label={labelMap[field]} value={formData[field] || ""} onChange={handleInputChange(field)} required variant="outlined" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff3b30' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff3b30' }, }, '& .MuiInputLabel-root': { color: '#666', '&.Mui-focused': { color: '#ff3b30' }, fontSize: '0.875rem', padding: '0 4px', backgroundColor: "white", }, }} />
                              </Grid>
                            ))}
                            <Grid item xs={12}>
                              <TextField fullWidth multiline minRows={3} variant="outlined" id="userMessage" size="small" label="Your Message" value={formData.userMessage} InputLabelProps={{ shrink: true, style: { fontSize: '0.875rem' } }} onChange={handleInputChange('userMessage')} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', border: "1px solid #cccccc", '& fieldset': { borderColor: '#ddd' }, '&:hover fieldset': { borderColor: '#ff3b30' }, '&.Mui-focused fieldset': { borderColor: '#ff3b30' } }, '& .MuiInputBase-input': { padding: '8.5px 14px', fontSize: '0.875rem', lineHeight: '1.5', minHeight: 'unset' }, '& .MuiInputLabel-root': { color: '#666', fontSize: '0.875rem', transform: 'translate(14px, -9px) scale(0.75)', padding: '0 4px', backgroundColor: "white", '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' } } }} />
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <Button fullWidth variant="contained" size="medium" onClick={handleSubmitInquiry} disabled={!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.emailId || isSubmitting} sx={{ background: 'linear-gradient(45deg, #ff3b30 0%, #ff5e56 100%)', color: 'white', py: 1.5, borderRadius: '6px', fontWeight: 'bold', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(255, 59, 48, 0.3)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(255, 59, 48, 0.4)' }, '&:disabled': { background: '#e0e0e0' } }}>
                                {isSubmitting ? (<><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> Submitting...</>) : ('Submit Inquiry')}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {inquiryType === 'shipping' && (
                        <Box>
                          {/* Step 1: Country Selection */}
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: formData.country ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                            borderRadius: '10px',
                            border: `1.5px solid ${formData.country ? '#4caf50' : '#ff3b30'}`,
                            transition: 'all 0.2s'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: formData.country ? '#4caf50' : '#ff3b30',
                              mb: 1,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              {formData.country ? <CheckCircleOutlineIcon fontSize="small" /> : <LocationOnIcon fontSize="small" />}
                              {formData.country ? 'Country Selected' : 'Step 1: Select Destination'}
                            </Typography>

                            <FormControl fullWidth size="small">
                              <InputLabel
                                shrink
                                sx={{
                                  fontSize: '0.875rem',
                                  color: '#666',
                                  padding: '0 4px',
                                  backgroundColor: 'white',
                                  '&.Mui-focused': { color: '#ff3b30' }
                                }}
                              >
                                Country
                              </InputLabel>
                              <Select
                                value={formData.country?.countryId || ''}
                                onChange={(e) => {
                                  const selected = countriesList.find(c => c.countryId === e.target.value);
                                  handleChange({ target: { name: 'country', value: selected } });
                                }}
                                onOpen={() => {
                                  if (countriesList.length === 0) fetchCountries();
                                }}
                                displayEmpty
                                sx={{
                                  borderRadius: '6px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#ddd'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#ddd'
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#ff3b30'
                                  }
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      maxHeight: 300,
                                      '& .MuiMenuItem-root': {
                                        fontSize: '0.875rem'
                                      }
                                    }
                                  }
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select a country</em>
                                </MenuItem>
                                {countriesList.map((country) => (
                                  <MenuItem key={country.countryId} value={country.countryId}>
                                    {country.countryName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          {/* Step 2: Port Selection */}
                          {formData.country && (
                            <Box sx={{
                              mb: 3,
                              p: 2,
                              backgroundColor: formData.port ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                              borderRadius: '10px',
                              border: `1.5px solid ${formData.port ? '#4caf50' : '#ff3b30'}`,
                              transition: 'all 0.2s'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: formData.port ? '#4caf50' : '#ff3b30',
                                mb: 1,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                {formData.port ? <CheckCircleOutlineIcon fontSize="small" /> : <DirectionsBoatIcon fontSize="small" />}
                                {formData.port ? 'Port Selected' : 'Step 2: Select Port'}
                              </Typography>

                              <FormControl fullWidth size="small" disabled={!formData.country}>
                                <InputLabel
                                  shrink
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: '#666',
                                    padding: '0 4px',
                                    backgroundColor: 'white',
                                    '&.Mui-focused': { color: '#ff3b30' }
                                  }}
                                >
                                  Port
                                </InputLabel>
                                <Select
                                  value={formData.port?.portId || ''}
                                  onChange={(e) => {
                                    const selected = portsList.find(p => p.portId === e.target.value);
                                    handleChange({ target: { name: 'port', value: selected } });
                                  }}
                                  onOpen={() => {
                                    if (formData.country && portsList.length === 0) {
                                      fetchPorts(formData.country.countryId);
                                    }
                                  }}
                                  displayEmpty
                                  sx={{
                                    borderRadius: '6px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#ddd'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#ddd'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#ff3b30'
                                    }
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        maxHeight: 300,
                                        '& .MuiMenuItem-root': {
                                          fontSize: '0.875rem'
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Select a port</em>
                                  </MenuItem>
                                  {portsList.map((port) => (
                                    <MenuItem key={port.portId} value={port.portId}>
                                      {port.portName}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}

                          {/* Step 3: Shipping Type Selection */}
                          {formData.port && (
                            <Box sx={{
                              mb: 3,
                              p: 2,
                              backgroundColor: formData.shippingType ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                              borderRadius: '10px',
                              border: `1.5px solid ${formData.shippingType ? '#4caf50' : '#ff3b30'}`,
                              transition: 'all 0.2s'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: formData.shippingType ? '#4caf50' : '#ff3b30',
                                mb: 1,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                {formData.shippingType ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                                {formData.shippingType ? 'Shipping Type Selected' : 'Step 3: Select Shipping Type'}
                              </Typography>

                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    onClick={() => handleShippingTypeChange('individual')}
                                    sx={{
                                      p: 2,
                                      border: formData.shippingType === 'individual' ? '2px solid #ff3b30' : '2px solid #ddd',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      textAlign: 'center',
                                      backgroundColor: formData.shippingType === 'individual' ? 'rgba(255, 59, 48, 0.05)' : 'white',
                                      transition: 'all 0.3s',
                                      '&:hover': {
                                        borderColor: '#ff3b30',
                                        backgroundColor: 'rgba(255, 59, 48, 0.05)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(255, 59, 48, 0.2)'
                                      }
                                    }}
                                  >
                                    <LocalShippingIcon sx={{ fontSize: 40, mb: 1, color: formData.shippingType === 'individual' ? '#ff3b30' : '#666' }} />
                                    <Typography variant="h6" fontWeight="700" fontSize="1rem">
                                      Individual Shipment
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                      CIF, C&F, FOB Options
                                    </Typography>
                                  </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Box
                                    onClick={() => handleShippingTypeChange('container')}
                                    sx={{
                                      p: 2,
                                      border: formData.shippingType === 'container' ? '2px solid #ff3b30' : '2px solid #ddd',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      textAlign: 'center',
                                      backgroundColor: formData.shippingType === 'container' ? 'rgba(255, 59, 48, 0.05)' : 'white',
                                      transition: 'all 0.3s',
                                      '&:hover': {
                                        borderColor: '#ff3b30',
                                        backgroundColor: 'rgba(255, 59, 48, 0.05)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(255, 59, 48, 0.2)'
                                      }
                                    }}
                                  >
                                    <InventoryIcon sx={{ fontSize: 40, mb: 1, color: formData.shippingType === 'container' ? '#ff3b30' : '#666' }} />
                                    <Typography variant="h6" fontWeight="700" fontSize="1rem">
                                      Container Shipment
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                      20ft & 40ft Containers
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          )}

                          {/* Step 4a: Individual Shipping Options (CIF, C&F, FOB) */}
                          {formData.shippingType === 'individual' && (
                            <Box sx={{
                              mb: 3,
                              p: 2,
                              backgroundColor: formData.shippingOption ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                              borderRadius: '10px',
                              border: `1.5px solid ${formData.shippingOption ? '#4caf50' : '#ff3b30'}`,
                              transition: 'all 0.2s'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: formData.shippingOption ? '#4caf50' : '#ff3b30',
                                mb: 1,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                {formData.shippingOption ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                                {formData.shippingOption ? 'Shipping Selected' : 'Step 4: Shipping Method'}
                              </Typography>

                              <RadioGroup
                                value={formData.shippingOption || ''}
                                onChange={async (e) => {
                                  const newShippingOption = e.target.value;
                                  const updatedFormData = {
                                    ...formData,
                                    shippingOption: newShippingOption
                                  };

                                  setFormData(updatedFormData);

                                  if (updatedFormData.port && newShippingOption) {
                                    try {
                                      const shippingData = await calculateShippingPrice({
                                        carModelId: product?.carModelId || '',
                                        portId: updatedFormData.port?.portId || '',
                                        shippingOptionType: newShippingOption,
                                        isInquiry: false,
                                        isContainerShipping: false,
                                        is20ft: false,
                                        is40ft: false,
                                        firstName: updatedFormData.firstName || '',
                                        lastName: updatedFormData.lastName || '',
                                        mobileNo: updatedFormData.phoneNumber || '',
                                        emailId: updatedFormData.emailId || '',
                                        userMessage: updatedFormData.userMessage || ''
                                      });

                                      setFormData(prev => ({
                                        ...prev,
                                        shippingCalculation: {
                                          volumeCubicMeters: shippingData.volumeCubicMeters,
                                          totalCost: shippingData.totalCost,
                                          totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
                                          error: null
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Error calculating shipping:', error);
                                      if (error.response?.status === 401) {
                                        setFormData(prev => ({
                                          ...prev,
                                          shippingCalculation: {
                                            ...prev.shippingCalculation,
                                            totalCost: null,
                                            totalCostIncludingBasePrice: null,
                                            error: 'UNAUTHORIZED'
                                          }
                                        }));
                                      }
                                    }
                                  }
                                }}
                                sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                              >
                                {shippingOptions.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                      <Radio
                                        size="small"
                                        sx={{
                                          color: '#ff3b30',
                                          '&.Mui-checked': { color: '#ff3b30' },
                                          padding: '6px'
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{
                                        border: formData.shippingOption === option.value ?
                                          '1.5px solid #ff3b30' : '1px solid #ddd',
                                        borderRadius: '6px',
                                        p: 1,
                                        backgroundColor: formData.shippingOption === option.value ?
                                          'rgba(255, 59, 48, 0.05)' : 'white',
                                        flex: 1,
                                        transition: 'all 0.2s'
                                      }}>
                                        <Typography fontWeight="600" fontSize="0.875rem">
                                          {option.label.split(' (')[0]}
                                        </Typography>
                                        <Typography fontSize="0.75rem" color="text.secondary">
                                          {option.label.includes('(') ? option.label.split(' (')[1].replace(')', '') : ''}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{
                                      m: 0,
                                      flex: 1,
                                      minWidth: '150px',
                                      '& .MuiFormControlLabel-label': {
                                        flex: 1,
                                        marginLeft: '4px'
                                      }
                                    }}
                                  />
                                ))}
                              </RadioGroup>
                            </Box>
                          )}

                          {/* Step 4b: Container Size Selection */}
                          {formData.shippingType === 'container' && (
                            <Box sx={{
                              mb: 3,
                              p: 2,
                              backgroundColor: formData.containerType ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                              borderRadius: '10px',
                              border: `1.5px solid ${formData.containerType ? '#4caf50' : '#ff3b30'}`,
                              transition: 'all 0.2s'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: formData.containerType ? '#4caf50' : '#ff3b30',
                                mb: 1,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                {formData.containerType ? <CheckCircleOutlineIcon fontSize="small" /> : <InventoryIcon fontSize="small" />}
                                {formData.containerType ? 'Container Selected' : 'Step 4: Select Container Size'}
                              </Typography>

                              <RadioGroup
                                value={formData.containerType || ''}
                                onChange={(e) => handleContainerSelection(e.target.value)}
                                sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                              >
                                <FormControlLabel
                                  value="20ft"
                                  control={
                                    <Radio
                                      size="small"
                                      sx={{
                                        color: '#ff3b30',
                                        '&.Mui-checked': { color: '#ff3b30' },
                                        padding: '6px'
                                      }}
                                    />
                                  }
                                  label={
                                    <Box sx={{
                                      border: formData.containerType === '20ft' ?
                                        '1.5px solid #ff3b30' : '1px solid #ddd',
                                      borderRadius: '6px',
                                      p: 1,
                                      backgroundColor: formData.containerType === '20ft' ?
                                        'rgba(255, 59, 48, 0.05)' : 'white',
                                      flex: 1,
                                      transition: 'all 0.2s'
                                    }}>
                                      <Typography fontWeight="600" fontSize="0.875rem">
                                        20ft Container
                                      </Typography>
                                      <Typography fontSize="0.75rem" color="text.secondary">
                                        5.9m × 2.35m × 2.39m
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    m: 0,
                                    flex: 1,
                                    minWidth: '150px',
                                    '& .MuiFormControlLabel-label': {
                                      flex: 1,
                                      marginLeft: '4px'
                                    }
                                  }}
                                />
                                <FormControlLabel
                                  value="40ft"
                                  control={
                                    <Radio
                                      size="small"
                                      sx={{
                                        color: '#ff3b30',
                                        '&.Mui-checked': { color: '#ff3b30' },
                                        padding: '6px'
                                      }}
                                    />
                                  }
                                  label={
                                    <Box sx={{
                                      border: formData.containerType === '40ft' ?
                                        '1.5px solid #ff3b30' : '1px solid #ddd',
                                      borderRadius: '6px',
                                      p: 1,
                                      backgroundColor: formData.containerType === '40ft' ?
                                        'rgba(255, 59, 48, 0.05)' : 'white',
                                      flex: 1,
                                      transition: 'all 0.2s'
                                    }}>
                                      <Typography fontWeight="600" fontSize="0.875rem">
                                        40ft Container
                                      </Typography>
                                      <Typography fontSize="0.75rem" color="text.secondary">
                                        12m × 2.35m × 2.39m
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    m: 0,
                                    flex: 1,
                                    minWidth: '150px',
                                    '& .MuiFormControlLabel-label': {
                                      flex: 1,
                                      marginLeft: '4px'
                                    }
                                  }}
                                />
                              </RadioGroup>
                            </Box>
                          )}

                          {/* Step 5: Container Shipping Options */}
                          {formData.containerType && (
                            <Box sx={{
                              mb: 3,
                              p: 2,
                              backgroundColor: formData.containerShippingOption ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                              borderRadius: '10px',
                              border: `1.5px solid ${formData.containerShippingOption ? '#4caf50' : '#ff3b30'}`,
                              transition: 'all 0.2s'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: formData.containerShippingOption ? '#4caf50' : '#ff3b30',
                                mb: 1,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                {formData.containerShippingOption ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                                {formData.containerShippingOption ? 'Shipping Option Selected' : 'Step 5: Select Shipping Option'}
                              </Typography>

                              <RadioGroup
                                value={formData.containerShippingOption || ''}
                                onChange={(e) => handleContainerShippingOptionSelection(e.target.value)}
                                sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                              >
                                {shippingOptions.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                      <Radio
                                        size="small"
                                        sx={{
                                          color: '#ff3b30',
                                          '&.Mui-checked': { color: '#ff3b30' },
                                          padding: '6px'
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{
                                        border: formData.containerShippingOption === option.value ?
                                          '1.5px solid #ff3b30' : '1px solid #ddd',
                                        borderRadius: '6px',
                                        p: 1,
                                        backgroundColor: formData.containerShippingOption === option.value ?
                                          'rgba(255, 59, 48, 0.05)' : 'white',
                                        flex: 1,
                                        transition: 'all 0.2s'
                                      }}>
                                        <Typography fontWeight="600" fontSize="0.875rem">
                                          {option.label.split(' (')[0]}
                                        </Typography>
                                        <Typography fontSize="0.75rem" color="text.secondary">
                                          {option.label.includes('(') ? option.label.split(' (')[1].replace(')', '') : ''}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{
                                      m: 0,
                                      flex: 1,
                                      minWidth: '150px',
                                      '& .MuiFormControlLabel-label': {
                                        flex: 1,
                                        marginLeft: '4px'
                                      }
                                    }}
                                  />
                                ))}
                              </RadioGroup>
                            </Box>
                          )}

                          {/* Cost Summary */}
                          {(formData.shippingOption || formData.containerShippingOption) && (
                            <Box sx={{
                              mb: 3,
                              p: 2,
                              backgroundColor: 'rgba(255, 59, 48, 0.05)',
                              borderRadius: '10px',
                              border: '1.5px solid #ff3b30'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: '#ff3b30',
                                mb: 2,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                <AttachMoneyIcon fontSize="small" /> COST SUMMARY
                              </Typography>

                              <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={4}>
                                  <Box sx={{
                                    p: 1,
                                    backgroundColor: 'white',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    textAlign: 'center',
                                    height: '100%'
                                  }}>
                                    <Typography variant="body2" color="#666" fontWeight="500" fontSize="0.75rem">
                                      {formData.shippingType === 'container' ? 'Container Type' : 'Vehicle Price'}
                                    </Typography>
                                    <Typography variant="h6" color="#ff3b30" fontWeight="700" fontSize="1rem">
                                      {formData.shippingType === 'container'
                                        ? (formData.containerType === '20ft' ? '20ft Container' : '40ft Container')
                                        : `${product?.carPrice?.toFixed(2) || '0.00'}`}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Box sx={{
                                    p: 1,
                                    backgroundColor: 'white',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    textAlign: 'center',
                                    height: '100%'
                                  }}>
                                    <Typography variant="body2" color="#666" fontWeight="500" fontSize="0.75rem">
                                      Shipping Cost
                                    </Typography>
                                    <Typography variant="h6" color="#ff3b30" fontWeight="700" fontSize="1rem">
                                      {shouldShowShippingCostAsk()
                                        ? 'Ask'
                                        : formData.shippingCalculation?.totalCost
                                          ? `$${parseFloat(formData.shippingCalculation.totalCost).toFixed(2)}`
                                          : (formData.shippingType === 'container' ? 'Ask' : '$0.00')
                                      }
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Box sx={{
                                    p: 1,
                                    backgroundColor: 'white',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    textAlign: 'center',
                                    height: '100%'
                                  }}>
                                    <Typography variant="body2" color="#666" fontWeight="500" fontSize="0.75rem">
                                      Total Cost
                                    </Typography>
                                    <Typography variant="h6" color="#ff3b30" fontWeight="700" fontSize="1rem">
                                      {shouldShowAsk()
                                        ? 'Ask'
                                        : formData.shippingCalculation?.totalCostIncludingBasePrice
                                          ? `${formData.shippingCalculation.totalCostIncludingBasePrice.toFixed(2)}`
                                          : `${product?.carPrice?.toFixed(2) || "0.00"}`}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>

                              <Typography variant="caption" color="#666" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                {formData.shippingType === 'container'
                                  ? '* Container pricing includes shipping to destination port. Contact us for final quote.'
                                  : '* Final shipping costs will be confirmed after vehicle inspection'}
                              </Typography>
                            </Box>
                          )}

                          {/* Personal Information */}
                          {(formData.shippingOption || formData.containerShippingOption) && (
                            <Box sx={{
                              backgroundColor: 'rgba(255, 59, 48, 0.05)',
                              borderRadius: '10px',
                              padding: '16px',
                              border: '1px dashed rgba(255, 59, 48, 0.2)'
                            }}>
                              <Typography variant="subtitle1" sx={{
                                color: '#ff3b30',
                                mb: 2,
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '0.875rem'
                              }}>
                                <PersonOutlineIcon fontSize="small" /> Contact Information
                              </Typography>

                              <Grid container spacing={2}>
                                {['firstName', 'lastName', 'phoneNumber', 'emailId'].map((field) => (
                                  <Grid item xs={12} md={6} key={field}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label={labelMap[field]}
                                      value={formData[field]}
                                      onChange={handleInputChange(field)}
                                      required
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '6px',
                                          '& fieldset': { borderColor: '#ddd' },
                                          '&:hover fieldset': { borderColor: '#ff3b30' },
                                          '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                        },
                                        '& .MuiInputLabel-root': {
                                          color: '#666',
                                          '&.Mui-focused': { color: '#ff3b30' },
                                          fontSize: '0.875rem'
                                        }
                                      }}
                                    />
                                  </Grid>
                                ))}

                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    size="small"
                                    label="Your Message"
                                    value={formData.userMessage}
                                    onChange={handleInputChange('userMessage')}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        '& fieldset': { borderColor: '#ddd' },
                                        '&:hover fieldset': { borderColor: '#ff3b30' }
                                      },
                                      '& .MuiInputLabel-root': {
                                        color: '#666',
                                        fontSize: '0.875rem'
                                      }
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 1 }}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    size="medium"
                                    onClick={handleSubmitShippingInquiry}
                                    disabled={
                                      !formData.firstName ||
                                      !formData.lastName ||
                                      !formData.phoneNumber ||
                                      !formData.emailId ||
                                      !formData.port ||
                                      (!formData.shippingOption && !formData.containerShippingOption) ||
                                      isSubmitting
                                    }
                                    sx={{
                                      background: 'linear-gradient(45deg, #ff3b30 0%, #ff5e56 100%)',
                                      color: 'white',
                                      py: 1.5,
                                      borderRadius: '6px',
                                      fontWeight: 'bold',
                                      fontSize: '0.875rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      boxShadow: '0 2px 8px rgba(255, 59, 48, 0.3)',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(255, 59, 48, 0.4)'
                                      },
                                      '&:disabled': {
                                        background: '#e0e0e0'
                                      }
                                    }}
                                  >
                                    {isSubmitting ? (
                                      <>
                                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                        Processing...
                                      </>
                                    ) : (
                                      'Get Shipping Quote'
                                    )}
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      )}

                      <Snackbar open={showSuccessPopup} autoHideDuration={2000} onClose={() => { setShowSuccessPopup(false); handleModalClose(); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert onClose={() => { setShowSuccessPopup(false); handleModalClose(); }} severity="success" sx={{ width: '100%', backgroundColor: '#4caf50', color: 'white' }}>Inquiry sent successfully!</Alert></Snackbar>
                      <Snackbar open={showErrorPopup} autoHideDuration={6000} onClose={() => setShowErrorPopup(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert onClose={() => setShowErrorPopup(false)} severity="error" sx={{ width: '100%', backgroundColor: '#ff3b30', color: 'white' }}>Failed to submit inquiry. Please try again.</Alert></Snackbar>
                    </CardContent>
                  </Card>
                </>
              ) : (<Typography sx={{ p: 4 }}>Product not found</Typography>)}
            </Box>
          </Container>
        </Box>
      </Modal>

      {/* Confirmation Dialog (keep existing dialog code) */}
      <Modal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          overflow: 'hidden',
        }}>
          <Box sx={{
            px: 3,
            py: 2,

          }}>
            <Typography
              id="dialog-title"
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
              }}
            >
              {dialogType === "move"
                ? "Confirm Move to Cart"
                : "Remove from Wishlist"}
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 3 }}>
            <Typography id="dialog-description">
              {dialogType === "move"
                ? "Are you sure you want to move this item to your cart?"
                : "Are you sure you want to remove this item from your wishlist?"}
            </Typography>
          </Box>

          <Box sx={{
            px: 3,
            py: 2,

            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <Button
              onClick={() => setOpenDialog(false)}
              sx={{
                border: "1px solid rgb(54 54 54)",
                color: "#282828",
                padding: "0px 18px"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={
                dialogType === "move" ? handleConfirmMove : handleConfirmRemove
              }
              variant="contained"
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#b71c1c", // halka dark hover ke liye
                },
              }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      </Modal>
    </StyledCard>
  );
};

// Car Listing Table Component for GlobalSearch
const CarListingTable = ({
  cars,
  onCarClick,
  onAddToWishlist,
  onAddToCart,
  imageUrl,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const { enqueueSnackbar } = useSnackbar();
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const visibleColumns = isMobile ? 10 : isTablet ? 10 : 11;

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const { modelId } = useParams();
  const [imageError, setImageError] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [inquiryType, setInquiryType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isPortSelected, setIsPortSelected] = useState(false);
  const [shippingCostData, setShippingCostData] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentImageType, setCurrentImageType] = useState('exterior');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [productLoading, setProductLoading] = useState(true);
  const [selectedCarModelId, setSelectedCarModelId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [openSpecifications, setOpenSpecifications] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phoneNumber: "",
    description: "",
    country: null,
    port: null,
    userMessage: '',
    shippingOption: '',
    shippingType: null,
    containerType: null,
    containerShippingOption: null
  });

  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);

  const handlePortChange = (event, newValue) => {
    handleChange({ target: { name: 'port', value: newValue } });
    setIsPortSelected(!!newValue); // Enable button only when port is selected
  };

  const generateValidImageUrls = async (type, modelId, maxCount = 15) => {
    const baseUrl = `https://car.amar.co.jp/api/image/car-${type}-images-`;

    const urls = await Promise.allSettled(
      Array.from({ length: maxCount }, (_, i) =>
        checkImageExists(`${baseUrl}${i + 1}/get-image/${modelId}`)
      )
    );

    return urls
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);
  };

  const loadInteriorImages = async () => {
    if (!interiorLoaded) {
      const interiors = await generateValidImageUrls("interior", modelId);
      setInteriorImages(interiors);
      setInteriorLoaded(true);
    }
  };

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const [interiorLoaded, setInteriorLoaded] = useState(false);

  const [shippingType, setShippingType] = useState(null);
  const [containerType, setContainerType] = useState(null);
  const [containerShippingOption, setContainerShippingOption] = useState(null);


  const calculateShippingPrice = async (data) => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate-price', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to calculate shipping price');
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating shipping price:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (selectedCarModelId) {
      setOpen(true);
    }
  }, [selectedCarModelId]);

  // <------------------ user details api integration code start ---------------->

  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);

  // Add this function to fetch user details
  const fetchUserDetailsGlobal = async () => {
    try {
      // Get the token from localStorage or wherever you store it
      const token = localStorage.getItem('authToken') || 'eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJoYXJkaWtAZ3JldHRvbS5jb20iLCJleHAiOjE3NTQ4NzY2NjEsImlhdCI6MTc1MTI3NjY2MSwiYXV0aG9yaXRpZXMiOiIifQ.caR99Ex3qtHfp9tLCMbsDxf9uxt_KhK9IlDbrB9-rek498PHySU1-FQhmBmTtK947DnUBXKS6CwNy6PccCH-4A';

      const response = await fetch('https://car.amar.co.jp/api/authorization/user/get/user/details', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      return userData;

    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  };

  const handleShippingTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      shippingType: type,
      shippingOption: null,
      containerType: null,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerSelection = (containerType) => {
    setFormData(prev => ({
      ...prev,
      containerType,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerShippingOptionSelection = async (option) => {
    setFormData(prev => ({ ...prev, containerShippingOption: option }));

    if (formData.port && formData.containerType && option) {
      try {
        const shippingData = await calculateShippingPrice({
          carModelId: product?.carModelId || '',
          portId: formData.port?.portId || '',
          shippingOptionType: option,
          isInquiry: false,
          isContainerShipping: true,
          is20ft: formData.containerType === '20ft',
          is40ft: formData.containerType === '40ft',
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          mobileNo: formData.phoneNumber || '',
          emailId: formData.emailId || '',
          userMessage: formData.userMessage || ''
        });

        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: shippingData.volumeCubicMeters,
            totalCost: shippingData.totalCost,
            totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
            containerRate: shippingData.containerRate,
            containerType: shippingData.containerType,
            isContainerShipping: shippingData.isContainerShipping,
            error: null
          }
        }));
      } catch (error) {
        console.error('Error calculating container shipping:', error);
        if (error.response?.status === 401) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'UNAUTHORIZED'
            }
          }));
        } else if (error.response?.status === 404 || error.message?.includes('Container rate not found')) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'CONTAINER_RATE_NOT_FOUND'
            }
          }));
        }
      }
    }
  };

  // Add helper function to get selected option (around line 500)
  const getSelectedOption = () => {
    return formData.shippingType === 'container' ? formData.containerShippingOption : formData.shippingOption;
  };

  // Add helper function to check if shipping cost should show "Ask" (around line 505)
  const shouldShowAsk = () => {
    if (formData.shippingType === 'container') {
      return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
        formData.shippingCalculation?.error === 'UNAUTHORIZED' ||
        !formData.shippingCalculation?.totalCost;
    } else {
      const hasDimensionIssue = (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
        (!product?.carLength || !product?.carWidth || !product?.carHeight);
      const hasUnauthorizedError = formData.shippingCalculation?.error === 'UNAUTHORIZED';
      return hasDimensionIssue || hasUnauthorizedError;
    }
  };

  // Helper function to check if shipping cost should show "Ask" specifically
  const shouldShowShippingCostAsk = () => {
    if (formData.shippingType === 'container') {
      return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
        formData.shippingCalculation?.error === 'UNAUTHORIZED';
    } else {
      const hasDimensionIssue = (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
        (!product?.carLength || !product?.carWidth || !product?.carHeight);
      const hasUnauthorizedError = formData.shippingCalculation?.error === 'UNAUTHORIZED';
      return hasDimensionIssue || hasUnauthorizedError;
    }
  };

  const handleSubmitShippingInquiry = async () => {
    if (!formData.port || (!formData.shippingOption && !formData.containerShippingOption)) {
      setShowErrorPopup(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const isContainer = formData.shippingType === 'container';

      const shippingData = {
        carModelId: product?.carModelId,
        portId: formData.port.portId,
        shippingOptionType: isContainer ? formData.containerShippingOption : formData.shippingOption,
        isInquiry: true,
        isContainerShipping: isContainer,
        is20ft: isContainer && formData.containerType === '20ft',
        is40ft: isContainer && formData.containerType === '40ft',
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNo: formData.phoneNumber,
        emailId: formData.emailId,
        userMessage: formData.userMessage
      };

      const response = await calculateShippingPrice(shippingData);
      setShowSuccessPopup(true);

      setFormData(prev => ({
        ...prev,
        shippingCalculation: {
          ...prev.shippingCalculation,
          ...response
        }
      }));

    } catch (error) {
      console.error('Error submitting shipping inquiry:', error);
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this useEffect to populate form data when component mounts
  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        setIsLoadingUserDetails(true);
        const response = await fetchUserDetailsGlobal();
        const userDetails = response.userDetails;

        // Map API response to your form fields
        // Adjust the field names based on your API response structure
        setFormData(prevData => ({
          ...prevData,
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          phoneNumber: userDetails.userMobileNumber || '',
          emailId: userDetails.email || ''
        }));

      } catch (error) {
        console.error('Failed to load user details:', error);
        // Optionally show error message to user
      } finally {
        setIsLoadingUserDetails(false);
      }
    };

    // Only fetch if inquiryType is 'general' and form is empty
    if (inquiryType === 'general' && !formData.firstName) {
      loadUserDetails();
    }
  }, [inquiryType]); // Run when inquiryType changes

  // Your existing handleInputChange function
  const handleInputChangeGlobal = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Add this function to manually refresh user details
  const handleRefreshUserDetails = async () => {
    try {
      setIsSubmitting(true);
      const userDetails = await fetchUserDetailsGlobal();

      setFormData(prevData => ({
        ...prevData,
        firstName: userDetails.firstName || userDetails.first_name || '',
        lastName: userDetails.lastName || userDetails.last_name || '',
        phoneNumber: userDetails.phoneNumber || userDetails.phone || userDetails.mobile || '',
        emailId: userDetails.emailId || userDetails.email || userDetails.emailAddress || ''
      }));

      console.log('User details loaded successfully');

    } catch (error) {
      console.error('Failed to refresh user details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // <------------------ user details api integration code end ---------------->


  const [exteriorImages, setExteriorImages] = useState([]);
  const [interiorImages, setInteriorImages] = useState([]);

  const imageUrl1 = exteriorImages[0]; // First exterior image
  const imageUrl2 = exteriorImages[1]; // Second exterior image
  const imageUrl3 = exteriorImages[2];

  // Styles object with improved spacing and alignment
  const styles = {
    globalWrapper: {
      width: '100%',
      maxWidth: '100%',
      padding: 0,
    },
  };

  const currentModelId = modelId || cars?.carModelId;
  const countries = ["AUSTRALIA", "ARUBA", "BAHAMAS", "BERMUDA", "CANADA", "CHILE", "DENMARK", "DOMINICA", "ETHIOPIA", "FIJI"];
  const shippingOptions = [
    { value: "CIF", label: "CIF (Cost, Insurance and Freight)" },
    { value: "CNF", label: "C&F (Cost and Freight)" },
    { value: "FOB", label: "FOB (Free On Board)" }
  ];
  const feeConfigurations = [
    { key: 'INSURANCE', label: 'Insurance', apiKey: 'insuranceCost' },
    { key: 'DOCUMENTATION', label: 'Documentation', apiKey: 'documentationFee' },
    { key: 'CURRENCY_ADJUSTMENT_FACTOR', label: 'Currency Adjustment', apiKey: 'currencyAdjustmentFactor' },
    { key: 'BUNKER_ADJUSTMENT_FACTOR', label: 'Bunker Adjustment', apiKey: 'bunkerAdjustmentFactor' },
    { key: 'TERMINAL_HANDLING', label: 'Terminal Handling', apiKey: 'terminalHandlingCharge' },
    { key: 'CUSTOMS_CLEARANCE', label: 'Customs Clearance', apiKey: 'customsClearanceFee' },
    { key: 'baseCostPerM3', label: 'Base Cost per m³', apiKey: 'baseShippingCost', isBaseCost: true }
  ];

  const labelMap = {
    firstName: "First Name *",
    lastName: "Last Name *",
    phoneNumber: "Phone Number *",
    emailId: "Email Address *"
  };

  // Fetch countries when Country Dropdown is opened
  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch ports when Port Dropdown is opened
  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && { port: null }) // Reset port when country changes
    }));
  };

  const fetchUserDetails = async (token) => {
    try {
      const response = await axios.get('https://car.amar.co.jp/api/authorization/user/details', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserDetails(response.data.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      // If token is invalid, remove it
      localStorage.removeItem('token');
      setUserDetails(null);
    }
  };

  // First, check token when component mounts
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      fetchUserDetails(token).catch(error => {
        console.error('Failed to fetch user details dddd:', error);
        localStorage.removeItem('token'); // Remove invalid token
      });
    }
  }, []);

  // Send general inquiry with token
  const sendGeneralInquiry = async (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const response = await fetch('https://car.amar.co.jp/api/dashboard/inquiry/send-inquiry', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        emailId: formData.emailId,
        phoneNumber: formData.phoneNumber,
        description: formData.userMessage,
        modelId: parseInt(selectedCarModelId, 10) // Add modelId from URL params
      })
    });

    if (!response.ok) throw new Error('Inquiry failed');
  };

  // Send shipping inquiry with token
  const sendShippingInquiry = async (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const payload = {
      carModelId: parseInt(selectedCarModelId, 10), // Changed from modelId to selectedCarModelId
      portId: formData.port?.portId,
      shippingOption: formData.shippingOption,
      isSendInquiry: true,
      firstName: formData.firstName,
      lastName: formData.lastName,
      userMobileNumber: formData.phoneNumber,
      userMessage: formData.userMessage,
      userEmailId: formData.emailId
    };

    const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Shipping inquiry failed');
  };

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  // Handle form submission with token verification
  const handleSubmitInquiry = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      alert('Please login to submit inquiry');
      return;
    }

    setIsSubmitting(true);

    try {
      if (inquiryType === 'general') {
        await sendGeneralInquiry(token);
      } else {
        await sendShippingInquiry(token);
      }
      setShowSuccessPopup(true);
    } catch (error) {
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const calculateShippingCost = async () => {
      if (selectedCarModelId && formData.port?.portId && formData.shippingOption) { // Changed from modelId to selectedCarModelId
        try {
          const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate', {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              carModelId: parseInt(selectedCarModelId, 10), // Changed from modelId to selectedCarModelId
              portId: formData.port.portId,
              shippingOption: formData.shippingOption,
              isSendInquiry: false
            })
          });
          const data = await response.json();
          setShippingCostData(data);
        } catch (error) {
          console.error('Error calculating shipping cost:', error);
        }
      }
    };

    calculateShippingCost();
  }, [selectedCarModelId, formData.port, formData.shippingOption]);

  const handleModalOpen = () => setOpen(true);
  const handleModalClose = () => {
    setOpen(false);
    setSelectedCarModelId(null); // Reset the selected ID
  };

  useEffect(() => {
    const fetchProductData = async () => {
      const currentModelId = selectedCarModelId || modelId;
      if (!currentModelId) return;

      try {
        setProductLoading(true);
        const response = await axios.get(`https://car.amar.co.jp/api/dashboard/product/get/Model/details/${currentModelId}`);
        setProduct(response.data.data);
        setProductLoading(false);
      } catch (err) {
        setError('Failed to fetch product data. Please try again later.');
        setProductLoading(false);
        console.error('Error fetching product data:', err);
      }
    };

    fetchProductData();
  }, [modelId, selectedCarModelId]);




  const handleFavoriteClick = async (e, carModelId, isFavorite) => {
    e.stopPropagation();
    setSelectedModelId(carModelId);

    if (isFavorite) {
      // Show removal confirmation dialog
      setDialogType("remove");
      setOpenDialog(true);
    } else {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          navigate("/login");
          return;
        }
        const result = await dispatch(addToWishlist(carModelId));
        if (result.success) {
          enqueueSnackbar("Added to wishlist successfully", {
            variant: "success",
          });
        } else if (result.alreadyInWishlist) {
          enqueueSnackbar("This item is already in your wishlist", {
            variant: "info",
          });
        } else {
          enqueueSnackbar(result.message || "Failed to add to wishlist", {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Error adding to wishlist", { variant: "error" });
        console.error("Error adding to wishlist:", error);
      }
    }
  };

  const handleConfirmAction = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      if (dialogType === "remove") {
        // DELETE API call for removing from wishlist
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${selectedModelId}`,
          {
            method: "DELETE",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          enqueueSnackbar("Removed from wishlist successfully", {
            variant: "success",
          });
          dispatch(fetchWishlist());
        } else {
          const data = await response.json();
          enqueueSnackbar(data.message || "Failed to remove from wishlist", {
            variant: "error",
          });
        }
      } else if (dialogType === "move") {
        // POST API call for moving to cart
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${selectedModelId}&SelectType=MOVE_TO_CART`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`,
            },
            body: "",
          }
        );
        if (response.ok) {
          enqueueSnackbar("Item moved to cart successfully", {
            variant: "success",
          });
          dispatch(fetchWishlist());
          dispatch(fetchCart());
        } else {
          const data = await response.json();
          enqueueSnackbar(data.message || "Failed to move item to cart", {
            variant: "error",
          });
        }
      }
    } catch (error) {
      enqueueSnackbar("Error processing request", { variant: "error" });
      console.error("Error:", error);
    } finally {
      setOpenDialog(false);
      setSelectedModelId(null);
      setDialogType("");
    }
  };

  useEffect(() => {
    const currentId = selectedCarModelId || modelId;
    if (!currentId) return;

    const loadExteriorImages = async () => {
      const exteriors = await generateValidImageUrls("exterior", currentId); // Use currentId here
      setExteriorImages(exteriors);
    };

    loadExteriorImages();
  }, [selectedCarModelId, modelId]); // Add both dependencies

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader sx={{ minWidth: isMobile ? "650px" : "800px" }}>
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell align="center">
              Drive This Beauty
            </StyledTableHeaderCell>
            {visibleColumns > 1 && (
              <StyledTableHeaderCell align="center">
                Referance Number
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 2 && (
              <StyledTableHeaderCell align="center">
                Chassis Number
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 3 && (
              <StyledTableHeaderCell align="center">Registration Year</StyledTableHeaderCell>
            )}
            {visibleColumns > 4 && (
              <StyledTableHeaderCell align="center">
                Engine CC / Motor kW
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 5 && (
              <StyledTableHeaderCell align="center">Fuel Type</StyledTableHeaderCell>
            )}
            {/* {visibleColumns > 6 && (
              <StyledTableHeaderCell align="center">A/C</StyledTableHeaderCell>
            )} */}
            {visibleColumns > 7 && (
              <StyledTableHeaderCell align="center">
                Body Color
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 8 && (
              <StyledTableHeaderCell align="center">
                Drive Type
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 9 && (
              <StyledTableHeaderCell align="center">
                Price
              </StyledTableHeaderCell>
            )}
            <StyledTableHeaderCell align="center">
              Actions
            </StyledTableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {cars.map((car) => {
            const isFavorite = wishlistItems.some(
              (item) => item.modelId === car.carModelId
            );
            const carImageUrl = imageUrl[car.carModelId] || "/carousel/carslider.png";

            return (
              <StyledTableRow
                key={car.carModelId}
                onClick={() => onCarClick(car.carModelId)}
              >
                <TableCell
                  sx={{
                    px: { xs: 1, sm: 2 },
                    // maxWidth: isMobile ? "120px" : "unset",
                    minWidth: isMobile ? "180px" : "250px",
                    maxWidth: isMobile ? "200px" : "300px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 1 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: isMobile ? 60 : 80,
                        height: isMobile ? 45 : 60,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={carImageUrl}
                        alt={`${car.carBrandName} ${car.carModelName}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 1,
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) =>
                          handleFavoriteClick(e, car.carModelId, isFavorite)
                        }
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          padding: isMobile ? "2px" : "3px",
                          backgroundColor: "rgba(255,255,255,0.8)",
                        }}
                      >
                        {isFavorite ? (
                          <Favorite sx={{ fontSize: isMobile ? 10 : 14 }} />
                        ) : (
                          <FavoriteBorder
                            sx={{ fontSize: isMobile ? 10 : 14 }}
                          />
                        )}
                      </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          lineHeight: 1.3,
                        }}
                      >
                        {car.carBrandName} {cleanModelName(car.carBrandName, car.carModelName)} {car.year}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* {visibleColumns > 1 && (
                  <TableCell sx={{ padding: isMobile ? "4px" : "8px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {car.ourPick && (
                        <Chip
                          label="Our Pick"
                          size="small"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.65rem",
                            height: isMobile ? "16px" : "24px",
                            color: "#dd006e", // Neon pink
                            backgroundColor: "#ffbddf",
                            fontWeight: "bold",
                            border: "1px solid #FF69B4",
                            "&:hover": { backgroundColor: "white" },
                          }}
                        />
                      )}
                      <Chip
                        label={`Grade ${car.carGrade || "A"}`}
                        size="small"
                        sx={{
                          fontSize: isMobile ? "0.55rem" : "0.65rem",
                          height: isMobile ? "16px" : "24px",
                          color: "#009966", // Neon mint
                          backgroundColor: "#9fffe7",
                          fontWeight: "bold",
                          border: "1px solid #00FFAA",
                          "&:hover": { backgroundColor: "white", },
                        }}
                      />
                      {car.specialDeal && (
                        <Chip
                          label="Special Offer"
                          size="small"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.65rem",
                            height: isMobile ? "16px" : "24px",
                            backgroundColor: "#d09fff",
                            color: "#6c2aab", // Periwinkle
                            fontWeight: "bold",
                            border: "1px solid #d09fff",
                            "&:hover": { backgroundColor: "white", color: "green" },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                )} */}


                {visibleColumns > 1 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carRefNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 2 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carChassisNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 3 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                    >
                      {car.year || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 4 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/engine.svg"
                        alt="Engine"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carEngineValue || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 5 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/MDI - gas-station.svg"
                        alt="Fuel"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carFuel || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 7 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? 12 : 16,
                          height: isMobile ? 12 : 16,
                          borderRadius: "50%",
                          backgroundColor:
                            car.carColor?.toLowerCase() || "#ccc",
                          border: "1px solid #ddd",
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                      >
                        {car.carColor || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                {visibleColumns > 8 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carDriveType || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 9 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile ? "0.75rem" : "0.95rem",
                        color: "error.main",
                      }}
                    >
                      {car.carPrice
                        ? `$${car.carPrice.toLocaleString()}`
                        : "On Request"}
                    </Typography>
                  </TableCell>
                )}

                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 0.8, // Slightly reduced gap between buttons
                      position: "relative",
                      padding: "6px 0", // Reduced padding
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small" // Already using small size
                      sx={{
                        bgcolor: "red", // Changed to red for consistency with seeAllPopularCarModels.js
                        color: "white", // Changed to white
                        border: "1px solid red", // Ensure solid border
                        borderRadius: "16px", // Smaller border radius
                        fontSize: "0.75rem", // Smaller font size
                        padding: "3px 12px", // Reduced padding
                        minWidth: "80px", // Reduced minimum width
                        height: "28px", // Explicitly set smaller height
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "red",
                          transform: "translateX(-100%)",
                          transition: "transform 0.3s ease",
                          zIndex: -1,
                        },
                        "&:hover": {
                          color: "white",
                          boxShadow: "0 3px 6px rgba(255,0,0,0.3)", // Smaller shadow
                          backgroundColor: "#cc0000", // Darker red on hover
                          "&::before": {
                            transform: "translateX(0)",
                          },
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Stop row click event
                        setSelectedCarModelId(car.carModelId); // Store the clicked car's model ID
                        handleModalOpen();
                      }}
                    >
                      INQUIRY
                    </Button>
                  </Box>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>







      {/* Modal - Inquiry Modal UI is already present below, no changes needed to the modal structure itself, only the button */}
      <InquiryModal
        open={open}
        onClose={handleModalClose}
        product={product}
        imageUrl1={imageUrl?.[selectedCarModelId] || "/carousel/carslider.png"}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedModelId(null);
          setDialogType("");
        }}
      >
        <DialogTitle>
          {dialogType === "move"
            ? "Confirm Move to Cart"
            : "Remove from Wishlist"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogType === "move"
              ? "Are you sure you want to move this item to your cart?"
              : "Remove this item from wishlist?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setOpenDialog(false);
              setSelectedModelId(null);
              setDialogType("");
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleConfirmAction}
            color="primary"
            variant="contained"
          >
            Confirm
          </MuiButton>
        </DialogActions>
      </Dialog>
    </StyledTableContainer>
  );
};

const FilterByGlobalSearch = () => {
  // --- State Hooks ---
  const [imageCache, setImageCache] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  // const [viewMode, setViewMode] = useState("list");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewMode, setViewMode] = useState(isMobile ? "grid" : "list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');

  // Single source of truth for filter state
  const [filterState, setFilterState] = useState({
    selectedCategory: null,
    selectedBrand: null,
    selectedModels: [],
    selectedYears: [],
    colorSelect: '',
    carDriveType: '',
    carEngine: '',
    carFilterAccessories: [],
    carFuelType: '',
    carMileage: '',
    carPrice: '',
    carSeat: '',
    carSteering: '',
    carTransmission: '',
    globalSearch: ''
  });

  // Refs to prevent unnecessary effects
  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef(null);
  const initializationRef = useRef(false);
  const showAllCarsRequestedRef = useRef(false);
  const filterStateRef = useRef(filterState); // Add ref to track current filter state

  // --- External Hooks and Redux Selectors ---
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchResults = useSelector(state => state.searchResults);
  const totalItems = useSelector(state => state.totalItems);

  const finalResults = Array.isArray(searchResults) ? searchResults : searchResults?.data || [];

  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_BASE_URL || 'https://car.amar.co.jp'
    : 'https://car.amar.co.jp';

  // Keep filterStateRef updated
  useEffect(() => {
    filterStateRef.current = filterState;
  }, [filterState]);

  // --- Utility Callbacks ---
  const getCacheBustedUrl = useCallback((url) => {
    if (process.env.NODE_ENV === 'development') { return url; }
    const separator = url.includes('?') ? '&' : '?';
    const timestamp = new Date().getTime();
    return `${url}${separator}cacheBuster=${timestamp}`;
  }, []);

  const fetchCarImage = useCallback(async (carModelId) => {
    try {
      const cacheBustedUrl = getCacheBustedUrl(`${API_BASE_URL}/api/image/car-exterior-images-1/get-image/${carModelId}`);
      const response = await fetch(cacheBustedUrl, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      return "/carousel/carslider.png";
    } catch (error) {
      return "/carousel/carslider.png";
    }
  }, [API_BASE_URL, getCacheBustedUrl]);

  // FIXED: Enhanced URL parameter parsing with better handling of arrays
  const parseURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {
      selectedCategory: null,
      selectedBrand: null,
      selectedModels: [],
      selectedYears: [],
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: '',
      globalSearch: ''
    };

    // Parse category
    const vehicleType = searchParams.get('categoryName') || searchParams.get('vehicleType') || '';
    if (vehicleType) {
      params.selectedCategory = { label: vehicleType };
    }

    // Parse brand
    const brand = searchParams.get('brandName') || searchParams.get('brand') || '';
    if (brand) {
      params.selectedBrand = { label: brand };
    }

    // FIXED: Better model parsing - handle both array and comma-separated string
    let models = searchParams.getAll('model').filter(m => m && m.trim() !== '');
    if (models.length === 0) {
      const modelString = searchParams.get('modelNames') || searchParams.get('model');
      if (modelString) {
        models = modelString.split(',')
          .map(m => m.trim())
          .filter(m => m !== '');
      }
    }
    params.selectedModels = models.map(m => ({ carModelName: m }));

    // FIXED: Better year parsing - handle both array and comma-separated string
    // let years = searchParams.getAll('years')
    //   .filter(y => y)
    //   .map(y => parseInt(y))
    //   .filter(y => !isNaN(y));

    // if (years.length === 0) {
    //   const yearString = searchParams.get('years') || searchParams.get('year');
    //   if (yearString) {
    //     years = yearString.split(',')
    //       .map(y => parseInt(y.trim()))
    //       .filter(y => !isNaN(y));
    //   }
    // }
    let years = searchParams.getAll('years')
  .flatMap(y => y.includes(',') ? y.split(',') : [y])  // Handle both formats
  .map(y => parseInt(y.toString().trim()))
  .filter(y => !isNaN(y));

// Fallback to 'year' param if 'years' is empty
if (years.length === 0) {
  years = searchParams.getAll('year')
    .flatMap(y => y.includes(',') ? y.split(',') : [y])
    .map(y => parseInt(y.toString().trim()))
    .filter(y => !isNaN(y));
}
    params.selectedYears = years.map(y => ({ year: y }));

    // Parse all other filters
    params.globalSearch = searchParams.get('search') || '';
    params.colorSelect = searchParams.get('colorSelect') || '';
    params.carDriveType = searchParams.get('carDriveType') || '';
    params.carEngine = searchParams.get('carEngine') || '';
    params.carFuelType = searchParams.get('carFuelType') || '';
    params.carMileage = searchParams.get('carMileage') || '';
    params.carPrice = searchParams.get('carPrice') || '';
    params.carSeat = searchParams.get('carSeat') || '';
    params.carSteering = searchParams.get('carSteering') || '';
    params.carTransmission = searchParams.get('carTransmission') || '';

    // FIXED: Better accessories parsing
    const accessories = searchParams.getAll('carFilterAccessories')
      .filter(a => a && a.trim() !== '');
    params.carFilterAccessories = accessories;

    return params;
  }, [location.search]);

  // FIXED: Enhanced URL update function with proper array handling
  const updateURLWithFilters = useCallback((searchParams, page, size) => {
    const queryParams = new URLSearchParams();

    // Add category
    if (searchParams.selectedCategory?.label) {
      queryParams.append('categoryName', searchParams.selectedCategory.label);
      queryParams.append('vehicleType', searchParams.selectedCategory.label);
    }

    // Add brand
    if (searchParams.selectedBrand?.label) {
      queryParams.append('brandName', searchParams.selectedBrand.label);
      queryParams.append('brand', searchParams.selectedBrand.label);
    }

    // FIXED: Add models - both as individual params and comma-separated
    if (searchParams.selectedModels && Array.isArray(searchParams.selectedModels) && searchParams.selectedModels.length > 0) {
      searchParams.selectedModels.forEach(model => {
        if (model?.carModelName) {
          queryParams.append('model', model.carModelName);
        }
      });
      const modelNames = searchParams.selectedModels
        .map(m => m.carModelName)
        .filter(m => m)
        .join(',');
      if (modelNames) {
        queryParams.append('modelNames', modelNames);
      }
    }

    // FIXED: Add years - both as individual params and comma-separated
    // if (searchParams.selectedYears && Array.isArray(searchParams.selectedYears) && searchParams.selectedYears.length > 0) {
    //   searchParams.selectedYears.forEach(year => {
    //     if (year?.year) {
    //       queryParams.append('year', year.year.toString());
    //     }
    //   });
    //   const years = searchParams.selectedYears
    //     .map(y => y.year)
    //     .filter(y => y)
    //     .join(',');
    //   if (years) {
    //     queryParams.append('years', years);
    //   }
    // }
    if (searchParams.selectedYears && Array.isArray(searchParams.selectedYears) && searchParams.selectedYears.length > 0) {
  searchParams.selectedYears.forEach(year => {
    if (year?.year) {
      queryParams.append('years', year.year.toString());  // 'years' (plural) use karo
    }
  });
}

    // Add all other filters
    if (searchParams.colorSelect) queryParams.append('colorSelect', searchParams.colorSelect);
    if (searchParams.carDriveType) queryParams.append('carDriveType', searchParams.carDriveType);
    if (searchParams.carEngine) queryParams.append('carEngine', searchParams.carEngine);
    if (searchParams.carFuelType) queryParams.append('carFuelType', searchParams.carFuelType);
    if (searchParams.carMileage) queryParams.append('carMileage', searchParams.carMileage);
    if (searchParams.carPrice) queryParams.append('carPrice', searchParams.carPrice);
    if (searchParams.carSeat) queryParams.append('carSeat', searchParams.carSeat);
    if (searchParams.carSteering) queryParams.append('carSteering', searchParams.carSteering);
    if (searchParams.carTransmission) queryParams.append('carTransmission', searchParams.carTransmission);

    // Add accessories
    if (searchParams.carFilterAccessories && Array.isArray(searchParams.carFilterAccessories)) {
      searchParams.carFilterAccessories.forEach(accessory => {
        if (accessory) {
          queryParams.append('carFilterAccessories', accessory);
        }
      });
    }

    // Add global search
    if (searchParams.globalSearch) {
      queryParams.append('search', searchParams.globalSearch);
    }

    // Add pagination
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());

    const newParamsString = queryParams.toString();
    navigate({ search: `?${newParamsString}` }, {
      replace: true,
      state: { filterState: searchParams }
    });
  }, [navigate]);

  // FIXED: Enhanced fetch function with better filter detection
  const fetchResults = useCallback(async (params, pageIndex = null, pageSize = null, isInitialLoad = false, isShowAllCars = false) => {
    if (isFetchingRef.current) { return; }

    const page = pageIndex !== null ? pageIndex : Math.max(0, currentPage - 1);
    const size = pageSize !== null ? pageSize : itemsPerPage;

    // Normalize the search params
    const searchParams = {
      selectedCategory: params?.selectedCategory || null,
      selectedBrand: params?.selectedBrand || null,
      selectedModels: params?.selectedModels || [],
      selectedYears: params?.selectedYears || [],
      colorSelect: params?.colorSelect || '',
      carDriveType: params?.carDriveType || '',
      carEngine: params?.carEngine || '',
      carFilterAccessories: params?.carFilterAccessories || [],
      carFuelType: params?.carFuelType || '',
      carMileage: params?.carMileage || '',
      carPrice: params?.carPrice || '',
      carSeat: params?.carSeat || '',
      carSteering: params?.carSteering || '',
      carTransmission: params?.carTransmission || '',
      globalSearch: params?.globalSearch || ''
    };

    const fetchKey = JSON.stringify({ ...searchParams, page, size, isShowAllCars });
    if (lastFetchParamsRef.current === fetchKey && !isInitialLoad) { return; }

    // FIXED: Better detection of search criteria
    const hasSearchCriteria =
      searchParams.selectedCategory ||
      searchParams.selectedBrand ||
      (searchParams.selectedModels && searchParams.selectedModels.length > 0) ||
      (searchParams.selectedYears && searchParams.selectedYears.length > 0) ||
      searchParams.colorSelect ||
      searchParams.carDriveType ||
      searchParams.carEngine ||
      (searchParams.carFilterAccessories && searchParams.carFilterAccessories.length > 0) ||
      searchParams.carFuelType ||
      searchParams.carMileage ||
      searchParams.carPrice ||
      searchParams.carSeat ||
      searchParams.carSteering ||
      searchParams.carTransmission ||
      (searchParams.globalSearch && searchParams.globalSearch.trim() !== '');

    const shouldFetch = hasSearchCriteria || isInitialLoad || isShowAllCars || showAllCarsRequestedRef.current;

    if (!shouldFetch && !isInitialLoad) {
      setIsLoading(false);
      return;
    }

    isFetchingRef.current = true;
    lastFetchParamsRef.current = fetchKey;
    setIsLoading(true);

    try {
      const response = await dispatch(performAdvanceFilter(
        searchParams.selectedCategory,
        searchParams.selectedBrand,
        searchParams.selectedModels,
        searchParams.selectedYears,
        page,
        size,
        searchParams.colorSelect,
        searchParams.carDriveType,
        searchParams.carEngine,
        searchParams.carFilterAccessories,
        searchParams.carFuelType,
        searchParams.carMileage,
        searchParams.carPrice,
        searchParams.carSeat,
        searchParams.carSteering,
        searchParams.carTransmission,
        searchParams.globalSearch
      ));

      if (response && response.data) {
        const newPage = page + 1;
        const newSize = size;

        if (newPage !== currentPage) setCurrentPage(newPage);
        if (newSize !== itemsPerPage) setItemsPerPage(newSize);

        if (response.totalPages) {
          setTotalPages(response.totalPages);
        } else {
          const totalFilteredItems = totalItems || (response.data.length > 0 ? response.data.length : 0);
          setTotalPages(Math.ceil(totalFilteredItems / size) || 1);
        }

        // FIXED: Properly update filter state
        setFilterState(searchParams);
        if (searchParams.globalSearch !== globalSearchTerm) {
          setGlobalSearchTerm(searchParams.globalSearch || '');
        }

        updateURLWithFilters(searchParams, newPage, newSize);

        if (isShowAllCars || showAllCarsRequestedRef.current) {
          showAllCarsRequestedRef.current = false;
        }
      } else {
        setTotalPages(1);
      }

    } catch (error) {
      console.error('Error fetching results:', error);
      enqueueSnackbar('Error fetching results. Please try again.', { variant: 'error' });
      setTotalPages(1);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [dispatch, enqueueSnackbar, updateURLWithFilters, totalItems, currentPage, itemsPerPage, globalSearchTerm]);

  // --- Effects ---
  useEffect(() => {
    const initializeData = async () => {
      if (initializationRef.current) return;
      initializationRef.current = true;

      const params = parseURLParams();
      const searchParams = new URLSearchParams(location.search);
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlSize = parseInt(searchParams.get('size')) || 20;

      const initialPage = Math.max(1, urlPage);
      const initialSize = Math.max(1, urlSize);

      setCurrentPage(initialPage);
      setItemsPerPage(initialSize);
      setFilterState(params);
      setGlobalSearchTerm(params.globalSearch || '');
      filterStateRef.current = params; // Initialize ref

      // FIXED: Better detection of filters in URL
      const hasFiltersInURL =
        params.selectedCategory ||
        params.selectedBrand ||
        (params.selectedModels && params.selectedModels.length > 0) ||
        (params.selectedYears && params.selectedYears.length > 0) ||
        params.colorSelect ||
        params.carDriveType ||
        params.carEngine ||
        (params.carFilterAccessories && params.carFilterAccessories.length > 0) ||
        params.carFuelType ||
        params.carMileage ||
        params.carPrice ||
        params.carSeat ||
        params.carSteering ||
        params.carTransmission ||
        params.globalSearch;

      const isShowAllCars = !hasFiltersInURL && (urlPage > 1 || urlSize !== 20);

      await fetchResults(params, initialPage - 1, initialSize, true, isShowAllCars);
      setHasInitialized(true);
    };
    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // FIXED: Enhanced URL change handler
  useEffect(() => {
    if (!hasInitialized || isFetchingRef.current) return;

    const handleURLChange = async () => {
      const newParams = parseURLParams();
      const searchParams = new URLSearchParams(location.search);
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlSize = parseInt(searchParams.get('size')) || 20;
      const newPage = Math.max(1, urlPage);
      const newSize = Math.max(1, urlSize);

      // FIXED: Deep comparison to detect actual changes
      const hasStateChanged = JSON.stringify(filterStateRef.current) !== JSON.stringify(newParams);

      if (hasStateChanged) {
        setFilterState(newParams);
        setGlobalSearchTerm(newParams.globalSearch || '');
        filterStateRef.current = newParams; // Update ref
        setCurrentPage(newPage);
        setItemsPerPage(newSize);

        const hasFiltersInURL =
          newParams.selectedCategory ||
          newParams.selectedBrand ||
          (newParams.selectedModels && newParams.selectedModels.length > 0) ||
          (newParams.selectedYears && newParams.selectedYears.length > 0) ||
          newParams.colorSelect ||
          newParams.carDriveType ||
          newParams.carEngine ||
          (newParams.carFilterAccessories && newParams.carFilterAccessories.length > 0) ||
          newParams.carFuelType ||
          newParams.carMileage ||
          newParams.carPrice ||
          newParams.carSeat ||
          newParams.carSteering ||
          newParams.carTransmission ||
          newParams.globalSearch;

        const isShowAllCars = !hasFiltersInURL;

        await fetchResults(newParams, newPage - 1, newSize, false, isShowAllCars);
      }
    };

    handleURLChange();
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (finalResults && finalResults.length > 0) {
      const newImageCache = { ...imageCache };
      let shouldUpdateCache = false;

      finalResults.forEach((result) => {
        if (!newImageCache[result.carModelId]) {
          newImageCache[result.carModelId] = "/carousel/carslider.png";
          shouldUpdateCache = true;
          fetchCarImage(result.carModelId).then(imageUrl => {
            setImageCache(prev => ({ ...prev, [result.carModelId]: imageUrl }));
          });
        }
      });

      if (shouldUpdateCache) {
        setImageCache(newImageCache);
      }
    }
  }, [finalResults, fetchCarImage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => {
      setShowFilter(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Event Handlers ---
  const handleGlobalSearchChange = (event) => {
    setGlobalSearchTerm(event.target.value);
  };

  const handleGlobalSearchSubmit = () => {
    const updatedState = { ...filterStateRef.current, globalSearch: globalSearchTerm };
    setFilterState(updatedState);
    filterStateRef.current = updatedState; // Update ref
    fetchResults(updatedState, 0, itemsPerPage, false, false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGlobalSearchSubmit();
    }
  };

  const handleShowAllCars = () => {
    const clearedState = {
      selectedCategory: null,
      selectedBrand: null,
      selectedModels: [],
      selectedYears: [],
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: '',
      globalSearch: ''
    };
    setFilterState(clearedState);
    filterStateRef.current = clearedState; // Update ref
    setGlobalSearchTerm('');
    showAllCarsRequestedRef.current = true;
    fetchResults(clearedState, 0, itemsPerPage, false, true);
  };

  // FIXED: Enhanced filter removal with proper state synchronization
  // FIXED: Enhanced filter removal with proper state synchronization
  const handleRemoveFilter = useCallback((type, value = null) => {
    const updatedState = { ...filterStateRef.current };

    switch (type) {
      case 'category':
        updatedState.selectedCategory = null;
        updatedState.selectedBrand = null;
        updatedState.selectedModels = [];
        updatedState.selectedYears = [];
        break;
      case 'brand':
        updatedState.selectedBrand = null;
        updatedState.selectedModels = [];
        updatedState.selectedYears = [];
        break;
      case 'model':
        updatedState.selectedModels = updatedState.selectedModels.filter(
          m => m.carModelName !== value
        );
        break;
      case 'year':
        updatedState.selectedYears = updatedState.selectedYears.filter(
          y => y.year !== value
        );
        break;
      case 'color':
        updatedState.colorSelect = '';
        break;
      case 'driveType':
        updatedState.carDriveType = '';
        break;
      case 'engine':
        updatedState.carEngine = '';
        break;
      case 'accessory':
        updatedState.carFilterAccessories = Array.isArray(updatedState.carFilterAccessories)
          ? updatedState.carFilterAccessories.filter(a => a !== value)
          : [];
        break;
      case 'fuelType':
        updatedState.carFuelType = '';
        break;
      case 'mileage':
        updatedState.carMileage = '';
        break;
      case 'price':
        updatedState.carPrice = '';
        break;
      case 'seat':
        updatedState.carSeat = '';
        break;
      case 'steering':
        updatedState.carSteering = '';
        break;
      case 'transmission':
        updatedState.carTransmission = '';
        break;
      case 'globalSearch':
        updatedState.globalSearch = '';
        setGlobalSearchTerm('');
        break;
      default:
        break;
    }

    // Update both state and ref synchronously
    setFilterState(updatedState);
    filterStateRef.current = updatedState;

    // Reset to first page when removing filters
    setCurrentPage(1);

    // Check if all filters are cleared after this removal
    const isAllFiltersCleared =
      !updatedState.selectedCategory &&
      !updatedState.selectedBrand &&
      (!updatedState.selectedModels || updatedState.selectedModels.length === 0) &&
      (!updatedState.selectedYears || updatedState.selectedYears.length === 0) &&
      !updatedState.colorSelect &&
      !updatedState.carDriveType &&
      !updatedState.carEngine &&
      (!updatedState.carFilterAccessories || updatedState.carFilterAccessories.length === 0) &&
      !updatedState.carFuelType &&
      !updatedState.carMileage &&
      !updatedState.carPrice &&
      !updatedState.carSeat &&
      !updatedState.carSteering &&
      !updatedState.carTransmission &&
      !updatedState.globalSearch;

    if (isAllFiltersCleared) {
      // When all filters are cleared, show all cars
      showAllCarsRequestedRef.current = true;
      fetchResults(updatedState, 0, itemsPerPage, false, true);
    } else {
      // Fetch with updated state
      fetchResults(updatedState, 0, itemsPerPage, false, false);
    }
  }, [fetchResults, itemsPerPage]);

  const handleClearAllFilters = () => {
    const clearedState = {
      selectedCategory: null,
      selectedBrand: null,
      selectedModels: [],
      selectedYears: [],
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: '',
      globalSearch: ''
    };
    setFilterState(clearedState);
    filterStateRef.current = clearedState; // Update ref
    setGlobalSearchTerm('');
    setCurrentPage(1);
    showAllCarsRequestedRef.current = true;
    fetchResults(clearedState, 0, itemsPerPage, false, true);
  };

  const handlePageChange = (event, page) => {
    if (event) event.preventDefault();
    if (page === currentPage) return;

    const hasActiveFilters =
      filterStateRef.current.selectedCategory ||
      filterStateRef.current.selectedBrand ||
      (filterStateRef.current.selectedModels && filterStateRef.current.selectedModels.length > 0) ||
      (filterStateRef.current.selectedYears && filterStateRef.current.selectedYears.length > 0) ||
      filterStateRef.current.colorSelect ||
      filterStateRef.current.carDriveType ||
      filterStateRef.current.carEngine ||
      (filterStateRef.current.carFilterAccessories && filterStateRef.current.carFilterAccessories.length > 0) ||
      filterStateRef.current.carFuelType ||
      filterStateRef.current.carMileage ||
      filterStateRef.current.carPrice ||
      filterStateRef.current.carSeat ||
      filterStateRef.current.carSteering ||
      filterStateRef.current.carTransmission ||
      filterStateRef.current.globalSearch;

    const isShowAllCars = !hasActiveFilters;
    fetchResults(filterStateRef.current, page - 1, itemsPerPage, false, isShowAllCars);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);

    const hasActiveFilters =
      filterStateRef.current.selectedCategory ||
      filterStateRef.current.selectedBrand ||
      (filterStateRef.current.selectedModels && filterStateRef.current.selectedModels.length > 0) ||
      (filterStateRef.current.selectedYears && filterStateRef.current.selectedYears.length > 0) ||
      filterStateRef.current.colorSelect ||
      filterStateRef.current.carDriveType ||
      filterStateRef.current.carEngine ||
      (filterStateRef.current.carFilterAccessories && filterStateRef.current.carFilterAccessories.length > 0) ||
      filterStateRef.current.carFuelType ||
      filterStateRef.current.carMileage ||
      filterStateRef.current.carPrice ||
      filterStateRef.current.carSeat ||
      filterStateRef.current.carSteering ||
      filterStateRef.current.carTransmission ||
      filterStateRef.current.globalSearch;

    const isShowAllCars = !hasActiveFilters;
    setCurrentPage(1);
    fetchResults(filterStateRef.current, 0, newItemsPerPage, false, isShowAllCars);
  };

  const handleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // FIXED: Enhanced apply filter handler with complete state sync
  const handleApplyFilter = (newFilterStateFromBasicSelect) => {
    console.log('Applying filter from BasicSelectFilter:', newFilterStateFromBasicSelect);

    const updatedState = {
      ...filterStateRef.current,
      ...newFilterStateFromBasicSelect,
      // Preserve global search if not explicitly changed
      globalSearch: newFilterStateFromBasicSelect.globalSearch !== undefined
        ? newFilterStateFromBasicSelect.globalSearch
        : filterStateRef.current.globalSearch
    };

    console.log('Updated filter state:', updatedState);

    // Reset page to 1 when applying new filters
    setCurrentPage(1);
    setFilterState(updatedState);
    filterStateRef.current = updatedState; // Update ref
    fetchResults(updatedState, 0, itemsPerPage, false, false);
  };

  const handleCarClick = (modelId) => {
    navigate(`/product/${modelId}`);
  };

  const handleAddToWishlist = async (modelId) => {
    enqueueSnackbar(`Added car ${modelId} to wishlist!`, { variant: 'info' });
  };

  const handleAddToCart = async (modelId) => {
    enqueueSnackbar(`Added car ${modelId} to cart!`, { variant: 'success' });
  };

  const renderFilterChips = () => {
    if (!filterState) return null;

    const carFilterAccessories = Array.isArray(filterState.carFilterAccessories)
      ? filterState.carFilterAccessories
      : [];

    const hasAdvancedFilters =
      filterState.colorSelect ||
      filterState.carDriveType ||
      filterState.carEngine ||
      carFilterAccessories.length > 0 ||
      filterState.carFuelType ||
      filterState.carMileage ||
      filterState.carPrice ||
      filterState.carSeat ||
      filterState.carSteering ||
      filterState.carTransmission;

    const hasBasicFilters =
      filterState.selectedCategory ||
      filterState.selectedBrand ||
      (filterState.selectedModels && filterState.selectedModels.length > 0) ||
      (filterState.selectedYears && filterState.selectedYears.length > 0);

    const hasGlobalSearch = filterState.globalSearch && filterState.globalSearch.trim() !== '';
    const shouldShowClearAll = hasBasicFilters || hasAdvancedFilters || hasGlobalSearch;

    // FIXED: Simplified chip deletion handlers - they will use the updated handleRemoveFilter
    const handleCategoryDelete = () => {
      handleRemoveFilter('category');
    };

    const handleBrandDelete = () => {
      handleRemoveFilter('brand');
    };

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
        {hasGlobalSearch && (
          <Chip
            label={`Search: ${filterState.globalSearch}`}
            onDelete={() => handleRemoveFilter('globalSearch')}
            sx={{
              backgroundColor: '#ffebee',
              '& .MuiChip-deleteIcon': { color: '#c62828' }
            }}
          />
        )}

        {filterState.selectedCategory && (
          <Chip
            label={`Body: ${filterState.selectedCategory.label}`}
            onDelete={handleCategoryDelete}
            sx={{
              backgroundColor: '#e3f2fd',
              '& .MuiChip-deleteIcon': { color: '#1976d2' }
            }}
          />
        )}

        {filterState.selectedBrand && (
          <Chip
            label={`Brand: ${filterState.selectedBrand.label}`}
            onDelete={handleBrandDelete}
            sx={{
              backgroundColor: '#e8f5e9',
              '& .MuiChip-deleteIcon': { color: '#2e7d32' }
            }}
          />
        )}

        {filterState.selectedModels?.map((model, index) => (
          <Chip
            key={`model-${index}`}
            label={`Model: ${model.carModelName}`}
            onDelete={() => handleRemoveFilter('model', model.carModelName)}
            sx={{
              backgroundColor: '#fff3e0',
              '& .MuiChip-deleteIcon': { color: '#e65100' }
            }}
          />
        ))}

        {filterState.selectedYears?.map((year, index) => (
          <Chip
            key={`year-${index}`}
            label={`Year: ${year.year}`}
            onDelete={() => handleRemoveFilter('year', year.year)}
            sx={{
              backgroundColor: '#f3e5f5',
              '& .MuiChip-deleteIcon': { color: '#7b1fa2' }
            }}
          />
        ))}

        {filterState.colorSelect && (
          <Chip
            label={`Color: ${filterState.colorSelect}`}
            onDelete={() => handleRemoveFilter('color')}
            sx={{
              backgroundColor: '#e0f7fa',
              '& .MuiChip-deleteIcon': { color: '#00838f' }
            }}
          />
        )}

        {filterState.carDriveType && (
          <Chip
            label={`Drive: ${filterState.carDriveType}`}
            onDelete={() => handleRemoveFilter('driveType')}
            sx={{
              backgroundColor: '#f1f8e9',
              '& .MuiChip-deleteIcon': { color: '#558b2f' }
            }}
          />
        )}

        {filterState.carEngine && (
          <Chip
            label={`Engine: ${filterState.carEngine}`}
            onDelete={() => handleRemoveFilter('engine')}
            sx={{
              backgroundColor: '#fffde7',
              '& .MuiChip-deleteIcon': { color: '#f9a825' }
            }}
          />
        )}

        {carFilterAccessories.map((accessory, index) => (
          <Chip
            key={`accessory-${index}`}
            label={`${accessory.replace(/_/g, ' ')}`}
            onDelete={() => handleRemoveFilter('accessory', accessory)}
            sx={{
              backgroundColor: '#fce4ec',
              '& .MuiChip-deleteIcon': { color: '#ad1457' }
            }}
          />
        ))}

        {filterState.carFuelType && (
          <Chip
            label={`Fuel: ${filterState.carFuelType}`}
            onDelete={() => handleRemoveFilter('fuelType')}
            sx={{
              backgroundColor: '#e8eaf6',
              '& .MuiChip-deleteIcon': { color: '#3949ab' }
            }}
          />
        )}

        {filterState.carMileage && (
          <Chip
            label={`Mileage: ${filterState.carMileage}`}
            onDelete={() => handleRemoveFilter('mileage')}
            sx={{
              backgroundColor: '#e0f2f1',
              '& .MuiChip-deleteIcon': { color: '#00897b' }
            }}
          />
        )}

        {filterState.carPrice && (
          <Chip
            label={`Price: ${filterState.carPrice}`}
            onDelete={() => handleRemoveFilter('price')}
            sx={{
              backgroundColor: '#fff8e1',
              '& .MuiChip-deleteIcon': { color: '#ff8f00' }
            }}
          />
        )}

        {filterState.carSeat && (
          <Chip
            label={`Seats: ${filterState.carSeat}`}
            onDelete={() => handleRemoveFilter('seat')}
            sx={{
              backgroundColor: '#efebe9',
              '& .MuiChip-deleteIcon': { color: '#6d4c41' }
            }}
          />
        )}

        {filterState.carSteering && (
          <Chip
            label={`Steering: ${filterState.carSteering}`}
            onDelete={() => handleRemoveFilter('steering')}
            sx={{
              backgroundColor: '#f5f5f5',
              '& .MuiChip-deleteIcon': { color: '#616161' }
            }}
          />
        )}

        {filterState.carTransmission && (
          <Chip
            label={`Transmission: ${filterState.carTransmission}`}
            onDelete={() => handleRemoveFilter('transmission')}
            sx={{
              backgroundColor: '#ede7f6',
              '& .MuiChip-deleteIcon': { color: '#5e35b1' }
            }}
          />
        )}

        {shouldShowClearAll && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAllFilters}
            sx={{
              ml: 1,
              textTransform: 'none',
              color: 'error.main',
              borderColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                borderColor: 'error.main'
              }
            }}
            startIcon={<ClearIcon fontSize="small" />}
          >
            Clear All
          </Button>
        )}
      </Box>
    );
  };

  const renderShowAllButton = () => {
    const hasAnyFilters =
      filterState.selectedCategory ||
      filterState.selectedBrand ||
      (filterState.selectedModels && filterState.selectedModels.length > 0) ||
      (filterState.selectedYears && filterState.selectedYears.length > 0) ||
      filterState.colorSelect ||
      filterState.carDriveType ||
      filterState.carEngine ||
      (filterState.carFilterAccessories && filterState.carFilterAccessories.length > 0) ||
      filterState.carFuelType ||
      filterState.carMileage ||
      filterState.carPrice ||
      filterState.carSeat ||
      filterState.carSteering ||
      filterState.carTransmission ||
      filterState.globalSearch;

    if (!hasAnyFilters && finalResults.length === 0 && !isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleShowAllCars}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              bgcolor: 'red',
              '&:hover': { bgcolor: '#b30000' }
            }}
          >
            Show All Cars
          </Button>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className="container-main-support" sx={{ width: "100%" }}>
      <Box sx={{ marginBottom: "0%" }}>
        <Notes />
      </Box>

      <Container maxWidth={false} sx={{ padding: 0, margin: 0 }}>

        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
          {renderFilterChips()}

          <Paper elevation={0} sx={{ /*p: 3, mb: 4,*/ borderRadius: '10px', backgroundColor: '#f9f9f9' }}>
            <Box sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              mb: isMobile ? 2 : 0,
              gap: isMobile ? 2 : 0
            }}>
              <Box>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Search Results
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {totalItems} {totalItems === 1 ? 'result' : 'results'} found
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
                  View:
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewMode}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&.Mui-selected': {
                        backgroundColor: '#ff3d00',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#dd3700',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="list">
                    <ListIcon />
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <GridView />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Paper>

          {isLoading ? (
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh"
            }}>
              <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
            </Box>
          ) : finalResults.length > 0 ? (
            viewMode === "list" ? (
              <CarListingTable
                cars={finalResults}
                onCarClick={handleCarClick}
                onAddToWishlist={handleAddToWishlist}
                onAddToCart={handleAddToCart}
                imageUrl={imageCache}
              />
            ) : (
              <Grid container spacing={3}>
                {finalResults.map((result) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={result.carModelId}>
                    <CarListingCardGrid
                      car={result}
                      onCarClick={handleCarClick}
                      onAddToWishlist={handleAddToWishlist}
                      onAddToCart={handleAddToCart}
                      imageUrl={imageCache[result.carModelId] || "/carousel/carslider.png"}
                    />
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              p: 4
            }}>
              <Typography variant="h6" sx={{ textAlign: "center", color: "#666", mb: 2 }}>
                No search results found
              </Typography>
              <Typography variant="body1" sx={{ textAlign: "center", color: "#888", mb: 3 }}>
                Try different search criteria or show all cars.
              </Typography>
              {renderShowAllButton()}
            </Box>
          )}

          {finalResults.length > 0 && totalPages > 1 && (
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              mb: 4,
              flexDirection: isMobile ? "column" : "row",
              gap: 2
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
                <Typography variant="body2" sx={{
                  color: "black",
                  fontWeight: 500,
                  letterSpacing: "0.5px"
                }}>
                  Results per page:{itemsPerPage}
                </Typography>
              </Box>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                  "& .MuiPaginationItem-root": {
                    fontWeight: 500,
                    borderColor: "black",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "rgba(255, 0, 0, 0.1)",
                      borderColor: "red",
                    },
                  },
                  "& .Mui-selected": {
                    backgroundColor: "red !important",
                    color: "white",
                    borderColor: "red",
                    "&:hover": {
                      backgroundColor: "#b30000 !important",
                      borderColor: "#800000",
                    },
                  },
                }}
              />

              <Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                variant="outlined"
                displayEmpty
                size="small"
                sx={{
                  width: 80,
                  color: "black",
                  fontWeight: 500,
                  backgroundColor: "#fff",
                  borderColor: "black",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "black",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "red",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "red",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "red",
                  },
                }}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </Box>
          )}
        </Container>

        <FooterManual />
      </Container>
    </Box>
  );
};

export default FilterByGlobalSearch;